import type { Action } from 'react-sweet-state';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidv4 } from 'uuid';

import { type DocNode } from '@atlaskit/adf-schema';
import { type AnalyticsEventPayload } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { Metric } from '@atlassian/browser-metrics/types';
import {
	getAIFCContentType,
	isCreatableAction,
	isUpdatableAction,
} from '@atlassian/conversation-assistant-action-helpers/utils';
import {
	customActionUpdater,
	getAccumulatedActionTypeGuard,
	getPrimaryAccumulatedValue,
	isAccumulatedAction,
	isConfluenceCreatableAction,
	isConfluenceDatabaseUpdateAction,
	isConfluencePageUpdateAction,
	isConfluenceWhiteboardUpdateAction,
	isDynamicUiCreatableAction,
	isImageCreatableAction,
	isRemixActionData,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import type { RovoApp } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store';
import { is3pApp } from '@atlassian/conversation-assistant-chat-prompt-input/utils/is-3p-app';
import { type CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import {
	type FollowUpSource,
	type InteractionSource,
} from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	ExperienceName,
	sendMessageExperienceName,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { type ExperienceTrackerAPI } from '@atlassian/conversation-assistant-instrumentation/main';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	type CitedProductList,
	type SourceProducts,
	type TrackAIEventPayloads,
} from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import { ASSISTANCE_SERVICE_ERROR_CODES } from '@atlassian/conversation-assistant-service-api/constants';
import {
	type AlphaExtendedThinkingMode,
	type Agent,
	type FetchStreamReturn,
	type FileUploadResponseItem,
	isChannelNotFoundError,
	isConversationChannelDataMessage,
	isFollowUpMessage,
	isSSEMessage,
	isStreamActionClassification,
	isStreamActionComplete,
	isStreamActionError,
	isStreamActionPart,
	isStreamActionPrepare,
	isStreamAnswerGenerationType,
	isStreamAnswerPart,
	isStreamAUPError,
	isStreamDynamicLayoutSubAgent,
	isStreamError,
	isStreamFastModeEnabled,
	isStreamFastModeResponseAvailable,
	isStreamRemoveAnswerNow,
	isStreamMemoryUpdate,
	isStreamReasoningPart,
	isStreamReconnectSupportPart,
	isStreamResearchPayload,
	isStreamResearchPlan,
	isStreamResearchStart,
	isStreamResponse,
	isStreamSkill,
	isStreamTrace,
	isStreamThinkDeeperTriggered,
	type LastAgentMessageStatus,
	type MessageFileId,
	type MessageType,
	type ParsedSSEMessage,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import {
	type Store as ChatContextStore,
	type ContentContext,
} from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import {
	type AdditionalAnalyticAttributes,
	type AgentMetadata,
	AnswerNowStatusType,
	CHAT_STORE_STATUS,
	type ChatStorePrivateState,
	type Conversation,
	type Trace,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { createAifcCreateInChatExperienceCollect } from '../../common/utils/aifc-experience-collect/create-aifc-create-in-chat-experience-collect';
import { createAifcExperienceCollect } from '../../common/utils/aifc-experience-collect/create-aifc-experience-collect';
import { messageLatencyMetrics } from '../../common/utils/browser-metrics';
import { type ContentFeatures, extractContentFeatures } from '../../common/utils/content-features';
import { randomIntFromInterval } from '../../common/utils/id-generator';
import { isRovoCustomAgent } from '../../common/utils/is-rovo-custom-agent';
import { DEFAULT_AGENT } from '../agents/constants';
import type { PromptStreamHook } from '../bridge-api/handlers/use-prompt-stream';
import { StreamLifecycleState } from '../bridge-api/streaming-lifecycle';
import { setLastMessageSentAt } from '../last-message-sent';

import { createDeadAirTracker } from './dead-air-tracker';
import {
	DeepResearchError,
	NoFinalResponseError,
	StreamDisconnectedError,
	StreamErrorException,
	StreamGeneratorError,
} from './error-classes';
import { minionVerificationAnalytics } from './minion-verification-analytics';
import { type ReconnectCompletionResult } from './reconnect-action';
import { createChatReconnectableStream } from './reconnectable-stream';
import { createStoreAbortController } from './store-abort-controller';
import { recordStreamMessage, resetStreamResponseRecording } from './stream-response-recorder';
import { type ChatStoreAction, type ChatStoreContainerProps } from './types';
import {
	addActionPart,
	addAnswerPart,
	addAnswerPartWithMarkdownData,
	addRequestIdToLatestHumanMessage,
	addSubAgentMessageToStoreAssistantMessage,
	clearStatus,
	clearStoreAssistantMessageContent,
	getStoreAssistantMessageContent,
	constructCitedProductList,
	constructSourceProducts,
	containsServiceDeskSource,
	createBroadcastStreamLifecycle,
	createStreamMarkdownProcessor,
	findLastAssistantAction,
	generateId,
	generateTmpMessageId,
	getConversation,
	appendCreatePageFollowUpIfNeeded,
	createSyntheticCreatePageFollowUp,
	shouldAppendCreatePageFollowUp,
	getLastAgentMessageID,
	getSourceAgentId,
	getTraceFromStreamMessage,
	handleStagingAreaActionValidation,
	isClientSideStreamingError,
	isEmptyConversation,
	markStreamingMessagesAsComplete,
	processStreamResponseMarkdown,
	replaceAnswerData,
	updateHumanMessageId,
	updateStoreAssistantMessageAnswerGenerationType,
	updateStoreAssistantMessageAnswerNowStatus,
	clearAnswerNowStatusForMessageIfSet,
	updateStoreAssistantMessageId,
	addMemorytoLatestAssistantMessage,
	type SkillAttributes,
	getActionPartKeyForMetrics,
	findStoreMessageIndex,
} from './utils';

export const dedupeTraceMessagesWithLatest = (traces: Trace[]): Trace[] => {
	const messages = traces.map((trace) => trace.messageContent);
	return traces.filter((trace, i) => messages.lastIndexOf(trace.messageContent) === i);
};

const getConversationTraces = (state: ChatStorePrivateState, conversationId: string) =>
	state.conversations[conversationId]?.traces ?? [];

/**
 * Helper function to stop/end metrics, that can only be called once
 */
const getMetricEndHelper = (metrics: any) => {
	let called = false;
	return (result: 'cancel' | 'stop', customData?: any): void => {
		if (!called) {
			if (result === 'stop') {
				metrics.stop({ customData });
			} else {
				metrics.cancel();
			}
			called = true;
		}
	};
};

/**
 * Helper function to clear traces only if they are not already empty,
 * avoiding unnecessary state updates and re-renders
 */
export const clearTracesIfNotEmpty = (
	getState: () => ChatStorePrivateState,
	conversationId: string,
	updateCurrentConversation: (data: Partial<Conversation>) => void,
): void => {
	const currentTraces = getConversationTraces(getState(), conversationId);
	if (currentTraces.length > 0) {
		updateCurrentConversation({
			traces: [],
		});
	}
};

/**
 * Util function to calculate the invokedFrom analytics attribute based on a message action key
 *
 * This attribute is used on the 'aiResult viewed' event to categorize an interaction into a subcategory for AI MAU metrics.
 * See here for more details on why these categories are defined based on actions in this way:
 * https://hello.atlassian.net/wiki/spaces/~701218777ef9283ff4740a03efb5d136b1ad0/pages/5869076758/What+experiences+should+count+for+AIFC+MAU
 * https://atlassian.slack.com/archives/C09BL4BF5L7/p1761669309117089?thread_ts=1759434177.613889&cid=C09BL4BF5L7
 *
 * This is exported only for testing purposes.
 */
export const getInvokedFrom = ({
	messageActionKey,
	messageActionData,
}: {
	messageActionKey: string | undefined;
	messageActionData?: unknown;
}): string | undefined => {
	// WARNING: Changing the logic here can affect AIFC MAU and other AI metrics.
	// Please consult with the Create with Rovo team before making any changes.
	if (!messageActionKey) {
		return undefined;
	}

	if (
		isDynamicUiCreatableAction({ key: messageActionKey }) ||
		isImageCreatableAction({ key: messageActionKey }) ||
		(isRemixActionData(messageActionData) && messageActionData.isRemixGenerated === true)
	) {
		return 'remix';
	}

	if (isConfluenceCreatableAction({ key: messageActionKey })) {
		// TODO All creatable actions should be as AIFC MAU here,
		// but we are temporarily changing this to Confluence only due to HOT-122605
		return 'AIFC';
	} else if (isConfluencePageUpdateAction({ key: messageActionKey })) {
		return 'Editor AI';
	} else if (isConfluenceWhiteboardUpdateAction({ key: messageActionKey })) {
		return 'Whiteboard AI';
	} else if (isConfluenceDatabaseUpdateAction({ key: messageActionKey })) {
		return 'Database AI';
	}
	return undefined;
};

/**
 * Util function to determine the page context where Rovo chat is being used.
 *
 * This uses the existing context objects (editorContext, whiteboardContext, databaseContext)
 * to determine the surface, and falls back to the browser URL when no context is available.
 *
 * This attribute is used on unified AI events (aiResult viewed, aiInteraction initiated)
 * to distinguish which surface Rovo chat was used on (e.g. edit page, view page, whiteboard, database).
 *
 * This is exported only for testing purposes.
 */
export const getPageContext = ({
	editorContext,
	whiteboardContext,
	databaseContext,
	browserUrl,
}: {
	editorContext?: { isViewMode?: boolean } | null;
	whiteboardContext?: { isViewMode?: boolean } | null;
	databaseContext?: Record<string, unknown> | null;
	browserUrl?: string;
}): string => {
	// Priority 1: Use existing context objects for reliable detection
	if (editorContext) {
		return editorContext.isViewMode ? 'confluence-page-view' : 'confluence-page-edit';
	}
	if (whiteboardContext) {
		return whiteboardContext.isViewMode ? 'whiteboard-view' : 'whiteboard-edit';
	}
	if (databaseContext) {
		return 'database';
	}

	// Priority 2: Fall back to URL-based detection when no context is available
	const url = browserUrl ?? (typeof window !== 'undefined' ? window.location.href : '');
	if (url.includes('/wiki/') && url.includes('/edit')) {
		return 'confluence-page-edit';
	}
	if (url.includes('/wiki/')) {
		return 'confluence-page-view';
	}
	if (url.includes('/whiteboard/')) {
		return 'whiteboard';
	}
	if (url.includes('/database/')) {
		return 'database';
	}

	return 'unknown';
};

export type MessageSource = {
	interactionSource: InteractionSource;
	followUpSource: FollowUpSource;
};

export type SendMessageAgentParam =
	| {
			agentId?: undefined;
			agent?: Agent;
	  }
	| {
			agentId: string;
			agent?: undefined;
	  };

export type ChatStoreActionsForSendMessage = {
	setConversationData: (params: {
		conversationId: string;
		data: Partial<Conversation>;
	}) => ChatStoreAction;
	createConversationOptimistically: (options: {
		name?: string;
		sourceId?: string;
		id?: string;
		timeCreated?: string;
		lastMessageTimestamp?: string;
		sourceAgentId?: string;
		status?: CHAT_STORE_STATUS;
		lastMessageType?: MessageType[];
		lastAgentMessageStatus?: LastAgentMessageStatus;
		agent?: AgentMetadata;
	}) => ChatStoreAction;
	addHumanMessage: (args_0: {
		id: string;
		content: DocNode;
		conversationId?: string;
		isFromSeededConversation?: boolean;
		requestId?: string;
		fileUploads?: MessageFileId[];
	}) => ChatStoreAction;
	addAssistantMessage: (args_0: {
		id: string;
		content: string | DocNode;
		conversationId?: string;
		agentNamedId?: string;
	}) => ChatStoreAction;
	clearFollowUpMessages: (args_0: { conversationId?: string }) => ChatStoreAction;
	setSelectedConversation: (args_0: {
		conversationId: string;
		sourceAgentId?: string;
	}) => ChatStoreAction;
	reconnectToInProgressStream: (args_0: {
		conversationId: string;
		messageId: string;
		answerIndex: number;
		lastEventId?: string;
		isDeepResearch?: boolean;
		abortCurrentRequest?: boolean;
		onComplete?: (result: ReconnectCompletionResult) => void;
	}) => ChatStoreAction;
};
export type SendMessageConsumerPramas = {
	prompt: DocNode;
	conversationId?: string;
	storeUserMessages: boolean;
	messageSources?: Partial<MessageSource>;
	files?: FileUploadResponseItem[];
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
	fastModeEnabled?: boolean;
	taskModeEnabled?: boolean;
	webSearchEnabled?: boolean;
	appFilters?: RovoApp[];
	addCurrentPageAsContextUserPreference?: boolean;
	productKey?: string;
	source?: string;
	promptStreamHook?: PromptStreamHook;
	creationContextParams?: CreationContextParams;
	additionalAnalyticAttributes?: AdditionalAnalyticAttributes;
	contentContext?: ContentContext;
	minionAlias?: string;
	onTempMessageIdReplacedOverride?: (oldId: string, newId: string) => void;
	openChat?: boolean;
	contentId?: string | number;
} & SendMessageAgentParam;

export type SendMessageParams = SendMessageConsumerPramas & ChatStoreActionsForSendMessage;

type SendMessageTrackingAttributes = {
	pluginsInvoked: Set<string>;
	skillsInvoked: SkillAttributes[];
	/** skillsInvoked is nested too deeply for Statsig, use executedStratusSkill to determine if at least one stratus skill has ran */
	executedStratusSkill: boolean;
	hasSources: boolean;
	messageId: string;
	messageSentTimeStamp: string;
	humanMessageId?: string;
	requestId?: string;
	messageActions?: string[];
	messageDynamicUIData?: {
		subtype?: string;
	}[];
	invokedFrom?: string;
	instrumentationId?: string;
	citedProductList: CitedProductList;
	sourceProducts: SourceProducts;
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	fastModeEnabled?: boolean;
	webSearchEnabled?: boolean;
	interactionSource?: InteractionSource;
	followUpSource?: FollowUpSource;
	contextProvided?: Record<keyof ChatContextStore, boolean>;
	sources?: string[];
	usesJsm?: boolean;
	isFirstPrompt: boolean;
	promptFeatures?: ContentFeatures;
	fileCount: number;
	appFilters: RovoApp[];
	appFilterCount: number;
	template: {
		templateId?: string;
		templateType?: string;
	};
	deepResearchInvoked: boolean;
	scenarioId?: string;
	pageContext?: string;
	forcedContentType?: string;
};

const getInitialTrackingAttributes: (
	agentId: string,
	instrumentationId: string,
	deepResearchEnabled: boolean | undefined,
	thinkDeeperEnabled: boolean | undefined,
	fastModeEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
	messageSources:
		| {
				interactionSource?: InteractionSource;
				followUpSource?: FollowUpSource;
		  }
		| undefined,
	isFirstPrompt: boolean,
	prompt: DocNode,
	files: FileUploadResponseItem[] | undefined,
	appFilters: RovoApp[] | undefined,
	template: {
		templateId?: string;
		templateType?: string;
	},
	forcedContentType: string | undefined,
) => SendMessageTrackingAttributes = (
	agentId: string,
	instrumentationId: string,
	deepResearchEnabled: boolean | undefined,
	thinkDeeperEnabled: boolean | undefined,
	fastModeEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
	messageSources:
		| { interactionSource?: InteractionSource; followUpSource?: FollowUpSource }
		| undefined,
	isFirstPrompt: boolean,
	prompt: DocNode,
	files: FileUploadResponseItem[] | undefined,
	appFilters: RovoApp[] | undefined,
	template: { templateId?: string; templateType?: string },
	forcedContentType: string | undefined,
) => {
	return {
		pluginsInvoked: new Set(),
		skillsInvoked: [],
		executedStratusSkill: false,
		hasSources: false,
		messageId: '',
		humanMessageId: '',
		requestId: undefined,
		messageActions: [],
		messageDynamicUIData: [],
		invokedFrom: undefined,
		instrumentationId,
		messageSentTimeStamp: new Date().toISOString(),
		citedProductList: {},
		sourceProducts: {},
		deepResearchEnabled,
		thinkDeeperEnabled,
		fastModeEnabled,
		webSearchEnabled,
		interactionSource: messageSources?.interactionSource,
		followUpSource: messageSources?.followUpSource ?? 'none',
		isFirstPrompt,
		promptFeatures: extractContentFeatures(prompt),
		fileCount: files?.length ?? 0,
		appFilters: appFilters ?? [],
		appFilterCount: appFilters?.length ?? 0,
		template,
		deepResearchInvoked: false,
		forcedContentType,
	};
};

const getOptimisticMessageTypes = (
	deepResearchEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
) => {
	const messageType: MessageType[] = [];

	if (fg('rovo_chat_chat_history_refresh')) {
		if (deepResearchEnabled) {
			messageType.push('DEEP_RESEARCH');
		}
		if (webSearchEnabled) {
			messageType.push('WEB_SEARCH');
		}
	}

	return { messageType };
};

const initLatencyMetrics = ({
	deepResearchEnabled,
	thinkDeeperEnabled,
	fastModeEnabled,
	agentId,
}: {
	deepResearchEnabled: boolean | undefined;
	thinkDeeperEnabled: boolean | undefined;
	fastModeEnabled: boolean | undefined;
	agentId: string | undefined;
}) => {
	const rovoCustomAgent = isRovoCustomAgent(agentId);
	const sendMessageLatencyMetrics = messageLatencyMetrics.createSendMessageMetrics({
		deepResearchEnabled,
		thinkDeeperEnabled,
		fastModeEnabled,
		isRovoCustomAgent: rovoCustomAgent,
	});
	const timeToFirstAnswerPartLatencyMetrics =
		messageLatencyMetrics.createTimeToFirstAnswerPartMetrics({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			isRovoCustomAgent: rovoCustomAgent,
		});
	const timeToFirstActionPartLatencyMetrics =
		messageLatencyMetrics.createTimeToFirstActionPartMetrics({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			isRovoCustomAgent: rovoCustomAgent,
		});
	const timeToActionCompletionLatencyMetrics =
		messageLatencyMetrics.createTimeToActionCompletionMetrics({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			isRovoCustomAgent: rovoCustomAgent,
		});

	const betweenChunksLatencyMetrics = messageLatencyMetrics.createBetweenChunksLatencyMetrics({
		deepResearchEnabled,
		thinkDeeperEnabled,
		fastModeEnabled,
	});

	sendMessageLatencyMetrics.start();
	timeToFirstAnswerPartLatencyMetrics.start();

	if (fg('aifc_create_enabled')) {
		timeToFirstActionPartLatencyMetrics.start();
		timeToActionCompletionLatencyMetrics.start();
	}

	const stopInteractionMetricsTimeToFirstAnswerPart = getMetricEndHelper(
		timeToFirstAnswerPartLatencyMetrics,
	);
	const stopInteractionMetricsTimeToFirstActionPart = getMetricEndHelper(
		timeToFirstActionPartLatencyMetrics,
	);
	const stopInteractionMetricsTimeToActionCompletion = getMetricEndHelper(
		timeToActionCompletionLatencyMetrics,
	);

	const cancelStreamingMetrics = () => {
		stopInteractionMetricsTimeToFirstAnswerPart('cancel');
		stopInteractionMetricsTimeToFirstActionPart('cancel');
		stopInteractionMetricsTimeToActionCompletion('cancel');
	};

	return {
		sendMessageLatencyMetrics,
		timeToFirstAnswerPartLatencyMetrics,
		timeToFirstActionPartLatencyMetrics,
		timeToActionCompletionLatencyMetrics,
		betweenChunksLatencyMetrics,
		stopInteractionMetricsTimeToFirstAnswerPart,
		stopInteractionMetricsTimeToFirstActionPart,
		stopInteractionMetricsTimeToActionCompletion,
		cancelStreamingMetrics,
	};
};

const hydrateStoreWithNewMessage = (
	dispatch: <T extends Action<ChatStorePrivateState, any, any>>(actionThunk: T) => ReturnType<T>,
	conversationId: string,
	messageType: MessageType[],
	tmpHumanMessageId: string,
	tmpAssistantMessageId: string,
	prompt: DocNode,
	files: FileUploadResponseItem[] | undefined,
	agentId: string,
	setConversationData: ChatStoreActionsForSendMessage['setConversationData'],
	addHumanMessage: ChatStoreActionsForSendMessage['addHumanMessage'],
	addAssistantMessage: ChatStoreActionsForSendMessage['addAssistantMessage'],
	clearFollowUpMessages: ChatStoreActionsForSendMessage['clearFollowUpMessages'],
) => {
	const { abortController, storeAbortController, confirmAbort } = createStoreAbortController();

	const updateCurrentConversation = (data: Partial<Conversation>) => {
		dispatch(
			setConversationData({
				conversationId,
				data,
			}),
		);
	};

	updateCurrentConversation({
		status: CHAT_STORE_STATUS.awaitingResponse,
		storeAbortController,
		lastMessageType: messageType,
		lastAgentMessageStatus: 'IN_PROGRESS',
	});
	dispatch(
		addHumanMessage({
			id: tmpHumanMessageId,
			content: prompt,
			conversationId,
			fileUploads: files,
		}),
	);
	dispatch(
		addAssistantMessage({
			id: tmpAssistantMessageId,
			content: '',
			conversationId,
			agentNamedId: agentId,
		}),
	);
	dispatch(clearFollowUpMessages({ conversationId }));

	return { abortController, storeAbortController, confirmAbort, updateCurrentConversation };
};

type HandleEditorContextAnalyticsParams = {
	source: string | undefined;
	contentContext: ContentContext;
	isFirstPrompt: boolean;
	userPreference: boolean;
	sendAnalyticsEvent: (payload: AnalyticsEventPayload) => void;
	activeTargetId?: boolean;
	isStagingArea?: boolean;
};

const handleEditorContextAnalytics = ({
	source,
	contentContext,
	isFirstPrompt,
	userPreference,
	sendAnalyticsEvent,
	activeTargetId,
	isStagingArea: stagingAreaFromStore,
}: HandleEditorContextAnalyticsParams) => {
	// If AIFC is not enabled, we should not send analytics
	if (!fg('aifc_create_enabled')) {
		return;
	}

	const inStagingArea = contentContext === 'staging-area';

	// Source values for Inline Rovo
	const sourceEligible =
		source === 'CONFLUENCE_INLINE_EDITOR' ||
		source === 'INLINE_EDITOR' ||
		source === 'CONFLUENCE_WHITEBOARD_TO_PAGE_BUTTON' ||
		source === 'CONFLUENCE_WHITEBOARD_BRAINSTORMING_FOLLOW_UP';

	const shouldProvideContext =
		// Inline Rovo
		sourceEligible ||
		// Within staging area & not the first prompt
		(inStagingArea && !isFirstPrompt) ||
		// Not within staging area, default to user preference
		(!inStagingArea && !!userPreference);

	// If there's no editor context when we should be providing it,
	// something went wrong so send analytics to track
	if (shouldProvideContext) {
		sendAnalyticsEvent({
			eventType: 'operational',
			action: 'missing',
			actionSubject: 'editorContext',
			attributes: {
				contentContext,
				source,
				userPreference,
				isFirstPrompt,
				activeTargetId,
				isStagingArea: stagingAreaFromStore,
			},
		});
	}
};

const populateSendMessageContext = async (
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'],
	isFirstPrompt: boolean,
	source: string | undefined,
	addCurrentPageAsContextUserPreference: boolean | undefined,
	contentContextValue: ContentContext,
	creationContextParams: CreationContextParams | undefined,
	productKey: string | undefined,
	sendAnalyticsEvent: (payload: AnalyticsEventPayload) => void,
	activeTargetId?: string | number,
) => {
	const editorContext = await chatContextStoreActions.getEditorContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const currentTarget = chatContextStoreActions.getTarget(contentContextValue, activeTargetId);

	// If there's no editor context available and the current target CAN
	// provide context, send analytics to track this error state
	if (!editorContext && currentTarget?.canProvideContext) {
		handleEditorContextAnalytics({
			isFirstPrompt,
			source,
			contentContext: contentContextValue,
			userPreference: !!addCurrentPageAsContextUserPreference,
			sendAnalyticsEvent,
			activeTargetId: !!activeTargetId,
			isStagingArea: chatContextStoreActions.getIsStagingArea(),
		});
	}

	const whiteboardContext = await chatContextStoreActions.getWhiteboardContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const slidesContext = await chatContextStoreActions.getSlidesContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const databaseContext = await chatContextStoreActions.getDatabaseContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const dashboardEditorContext = await chatContextStoreActions.getDashboardEditorContext({
		contentContext: contentContextValue,
		activeTargetId,
	});

	const browserContext = await chatContextStoreActions.getBrowserContext({
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		enabled: FeatureGates.getExperimentValue(
			'rovo_extension_dom_improvement',
			'rovo_extension_dom_context_improvement_enabled',
			false,
			{
				fireExperimentExposure: false,
			},
		),
	});

	const jiraContext = await (async () => {
		// Only fetch Jira context when in staging area and product is Jira
		// and not the first prompt (refinement messages)
		if (
			!isFirstPrompt &&
			(contentContextValue === 'staging-area' || fg('jira-aifc-enable-preview-card-in-chat-v2')) &&
			productKey === 'jira' &&
			fg('jira_aifc_enabled')
		) {
			return chatContextStoreActions.getJiraContext?.();
		}
		return undefined;
	})();

	const additionalCreationContext = editorContext?.additionalContext;
	if (editorContext && editorContext.additionalContext) {
		delete editorContext.additionalContext;
	}
	const useGenericEditorSkill = editorContext?.useGenericEditorSkill;
	if (editorContext && 'useGenericEditorSkill' in editorContext) {
		delete editorContext.useGenericEditorSkill;
	}

	const isViewMode = !!(editorContext?.isViewMode || whiteboardContext?.isViewMode);
	// Bookmark
	return {
		browserContext,
		editorContext,
		whiteboardContext,
		slidesContext,
		databaseContext,
		dashboardEditorContext,
		additionalContext: {
			spaceCreateContext: await chatContextStoreActions.getSpaceCreateContext({
				isFirstPrompt,
				contentContext: contentContextValue,
			}),
			creationContext: {
				...(creationContextParams ? creationContextParams : {}),
				isStagingArea: contentContextValue === 'staging-area',
				productKey,
				// When `contentMauiId` or `mediaFileId` is present, we don't need to send the source
				// Ideally we want to set it upstream, but we need to ensure we don't break existing functionality
				source:
					editorContext?.contentMauiId || editorContext?.mediaFileId
						? 'remix'
						: // If source is explicitly passed, use that, otherwise rely on creationContextParams.source which is set at creation entry point (e.g. 'remix', 'ai-first-creation')
						  source ?? creationContextParams?.source,
				isViewMode: expValEquals('cc-maui-experiment', 'isEnabled', true)
					? creationContextParams?.isViewMode !== undefined
						? creationContextParams.isViewMode
						: isViewMode
					: isViewMode,
				useGenericEditorSkill,
				additionalContext: additionalCreationContext,
				...(jiraContext && { jiraContext }),
				...(expValEquals('cc-maui-experiment', 'isEnabled', true) && {
					...(editorContext?.dynamicUiSource && {
						dynamicUiSource: {
							...editorContext.dynamicUiSource,
							...(creationContextParams?.dynamicUiSource?.fragmentAdf && {
								fragmentAdf: creationContextParams.dynamicUiSource.fragmentAdf,
							}),
						},
					}),
					dynamicUiType: editorContext?.dynamicUiType ?? creationContextParams?.dynamicUiType,
					contentMauiId: editorContext?.contentMauiId ?? creationContextParams?.contentMauiId,
					mediaFileId: editorContext?.mediaFileId ?? creationContextParams?.mediaFileId,
				}),
			},
			userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		},
		aiFeatureContext: chatContextStoreActions.getAIFeatureContext(),
		jiraWorkflowContext: chatContextStoreActions.getJiraWorkflowContext(),
		dashboardContext: chatContextStoreActions.getDashboardContext(),
		useCurrentBrowsingContext: chatContextStoreActions.useCurrentBrowsingContext(
			!!addCurrentPageAsContextUserPreference,
		),
	};
};

const handleSendMessageServiceCallFailure = (
	error: any,
	deepResearchEnabled: boolean | undefined,
	thinkDeeperEnabled: boolean | undefined,
	fastModeEnabled: boolean | undefined,
	webSearchEnabled: boolean | undefined,
	streamActionInfo: StreamActionInfo,
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'],
	experienceTracker: ExperienceTrackerAPI,
	sendMessageExperienceNameCompiled: string,
	trackingAttributes: SendMessageTrackingAttributes,
	updateCurrentConversation: (data: Partial<Conversation>) => void,
	sendMessageLatencyMetrics: Metric,
	cancelStreamingMetrics: () => void,
	broadcastStreamLifecycle: (status: StreamLifecycleState) => void,
	confirmAbort: () => void,
	trackAIMAUEvent: (event: TrackAIEventPayloads) => void,
	messageSources: Partial<MessageSource> | undefined,
	abortController: AbortController,
	hasLoggedInitiatedEvent: boolean,
	tmpAssistantMessageId: string,
	clearMessageResearch: (args_0: { messageId: string }) => void,
	getState: () => ChatStorePrivateState,
	conversationId: string,
	storeUserMessages: boolean,
) => {
	// Convert deep research related errors to DeepResearchError when deep research is enabled
	let convertedError = error;
	if (deepResearchEnabled) {
		convertedError = new DeepResearchError(error.message, {
			traceId: error.errorAttributes?.traceId,
			requestId: error.errorAttributes?.requestId,
		});
	}

	// Returns bad request error because agent is deactivated or user has no permission
	const isAgentDeactivated =
		error instanceof ApiError &&
		error.errorAttributes.errorCode?.includes(ASSISTANCE_SERVICE_ERROR_CODES.AGENT_DEACTIVATED);
	const isNoAgentPermission =
		error instanceof ApiError &&
		error.errorAttributes.errorCode?.includes(ASSISTANCE_SERVICE_ERROR_CODES.AGENT_FORBIDDEN) &&
		fg('convo-ai-enable-agents-use-permission');

	if (isAgentDeactivated || isNoAgentPermission) {
		experienceTracker.fail({
			name: sendMessageExperienceNameCompiled,
			error,
			attributes: {
				deepResearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				webSearchEnabled,
				llmDebugLogsEnabled: storeUserMessages,
				isStreamAction: streamActionInfo.isAction,
				actionKey: streamActionInfo.actionKey,
				featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
			},
		});

		if (!hasLoggedInitiatedEvent) {
			// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
			// Delaying the initiated event firing allows us to create the pairing appropriately,
			// for any initiated events leading up to an error or dismiss state with a
			// hashed ID
			trackAIMAUEvent({
				type: 'initiated',
				subjectId: messageSources?.interactionSource,
				proactive: false,
				userGenerated: true,
				timestamp: trackingAttributes.messageSentTimeStamp,
				singleInstrumentationID: trackingAttributes.instrumentationId,
				deepResearchEnabled: trackingAttributes.deepResearchEnabled,
				thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
				fastModeEnabled: trackingAttributes.fastModeEnabled,
				webSearchEnabled: trackingAttributes.webSearchEnabled,
				pageContext: trackingAttributes.pageContext,
			});
		}

		trackAIMAUEvent({
			type: 'error',
			userGenerated: true,
			messageId: trackingAttributes.requestId,
			timestamp: new Date().toISOString(),
			singleInstrumentationID: trackingAttributes.instrumentationId,
			deepResearchEnabled: trackingAttributes.deepResearchEnabled,
			thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
			fastModeEnabled: trackingAttributes.fastModeEnabled,
			webSearchEnabled: trackingAttributes.webSearchEnabled,
		});

		updateCurrentConversation({
			status: CHAT_STORE_STATUS.deactivatedAgentError,
			traces: [],
			errorCode: fg('convo-ai-enable-agents-use-permission')
				? error.errorAttributes.errorCode
				: undefined,
		});

		sendMessageLatencyMetrics.cancel();
		cancelStreamingMetrics();
		broadcastStreamLifecycle(StreamLifecycleState.ERRORED);
		return;
	} else if (error instanceof Error && error.name === 'AbortError') {
		// Check if the abort was programmatic or not. `abortController.signal.aborted` is a readonly field that is only set to true when called programmatically
		// The browser can abort requests to free up resources & save power (esp. on background tabs). When it does this, it **does not** set `abortController.signal.aborted` to true
		const isProgrammaticAbort = abortController.signal.aborted;

		experienceTracker.abort({
			name: sendMessageExperienceNameCompiled,
			reason: 'Aborted',
			attributes: {
				featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
				isProgrammaticAbort,
			},
		});

		if (!hasLoggedInitiatedEvent) {
			// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
			// Delaying the initiated event firing allows us to create the pairing appropriately,
			// for any initiated events leading up to an error or dismiss state with a
			// hashed ID
			trackAIMAUEvent({
				type: 'initiated',
				subjectId: messageSources?.interactionSource,
				proactive: false,
				userGenerated: true,
				timestamp: trackingAttributes.messageSentTimeStamp,
				singleInstrumentationID: trackingAttributes.instrumentationId,
				deepResearchEnabled: trackingAttributes.deepResearchEnabled,
				thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
				fastModeEnabled: trackingAttributes.fastModeEnabled,
				webSearchEnabled: trackingAttributes.webSearchEnabled,
				pageContext: trackingAttributes.pageContext,
			});
		}

		trackAIMAUEvent({
			type: 'dismissed',
			userGenerated: true,
			messageId: trackingAttributes.requestId,
			timestamp: new Date().toISOString(),
			singleInstrumentationID: trackingAttributes.instrumentationId,
			deepResearchEnabled: trackingAttributes.deepResearchEnabled,
			thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
			fastModeEnabled: trackingAttributes.fastModeEnabled,
			webSearchEnabled: trackingAttributes.webSearchEnabled,
		});

		updateCurrentConversation(clearStatus({ lastAgentMessageStatus: 'FAILED' }));
		clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
		clearMessageResearch({ messageId: tmpAssistantMessageId });
		sendMessageLatencyMetrics.cancel();
		cancelStreamingMetrics();
		broadcastStreamLifecycle(StreamLifecycleState.ABORTED);

		confirmAbort();

		return;
	} else {
		experienceTracker.fail({
			name: sendMessageExperienceNameCompiled,
			error,
			attributes: {
				deepResearchEnabled,
				fastModeEnabled,
				thinkDeeperEnabled,
				webSearchEnabled,
				llmDebugLogsEnabled: storeUserMessages,
				isStreamAction: streamActionInfo.isAction,
				actionKey: streamActionInfo.actionKey,
				featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
			},
		});

		if (!hasLoggedInitiatedEvent) {
			// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
			// Delaying the initiated event firing allows us to create the pairing appropriately,
			// for any initiated events leading up to an error or dismiss state with a
			// hashed ID
			trackAIMAUEvent({
				type: 'initiated',
				subjectId: messageSources?.interactionSource,
				proactive: false,
				userGenerated: true,
				timestamp: trackingAttributes.messageSentTimeStamp,
				singleInstrumentationID: trackingAttributes.instrumentationId,
				deepResearchEnabled: trackingAttributes.deepResearchEnabled,
				thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
				fastModeEnabled: trackingAttributes.fastModeEnabled,
				webSearchEnabled: trackingAttributes.webSearchEnabled,
				pageContext: trackingAttributes.pageContext,
			});
		}

		trackAIMAUEvent({
			type: 'error',
			userGenerated: true,
			messageId: trackingAttributes.requestId,
			timestamp: new Date().toISOString(),
			singleInstrumentationID: trackingAttributes.instrumentationId,
			deepResearchEnabled: trackingAttributes.deepResearchEnabled,
			thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
			fastModeEnabled: trackingAttributes.fastModeEnabled,
			webSearchEnabled: trackingAttributes.webSearchEnabled,
		});

		sendMessageLatencyMetrics.cancel();
		cancelStreamingMetrics();
		broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

		throw convertedError;
	}
};

export interface ProcessStreamParams {
	// Core stream params
	generator: AsyncGenerator<StreamMessage | ParsedSSEMessage<StreamMessage>, any, any>;
	conversationId: string;
	additionalAnalyticAttributes?: AdditionalAnalyticAttributes;
	// The assistant message ID to update during streaming.
	// For new messages: temporary ID that gets replaced with server ID.
	tmpAssistantMessageId: string;
	tmpHumanMessageId: string;
	// Message context
	prompt: DocNode;
	productKey: string | undefined;
	// The answer index to optimize message search.
	answerIndex: number;

	// Search Mode
	thinkDeeperEnabled: boolean | undefined;
	fastModeEnabled: boolean | undefined;
	deepResearchEnabled: boolean | undefined;
	webSearchEnabled: boolean | undefined;

	// State management
	updateCurrentConversation: (data: Partial<Conversation>) => void;
	getState: () => ChatStorePrivateState;
	setMessages: ChatStoreContainerProps['setMessages'];
	getMessages: ChatStoreContainerProps['getMessages'];

	// Tracking & analytics
	traceId: string | undefined;
	sendMessageExperienceNameCompiled: string;
	trackingAttributes: SendMessageTrackingAttributes;
	experienceTracker: ExperienceTrackerAPI;
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];
	trackAIMAUEvent: (event: TrackAIEventPayloads) => void;

	// Stream processing callbacks
	processStreamResearch: ChatStoreContainerProps['processStreamResearch'];
	processMessageStreamAction: ChatStoreContainerProps['processMessageStreamAction'];
	processMessageStreamComplete: ChatStoreContainerProps['processMessageStreamComplete'];
	processMessageStreamActionClassification: ChatStoreContainerProps['processMessageStreamActionClassification'];
	containerOnTempMessageIdReplaced: ChatStoreContainerProps['onTempMessageIdReplaced'];
	updateResearchMessageId: ChatStoreContainerProps['updateResearchMessageId'];
	onTempMessageIdReplacedOverride: SendMessageConsumerPramas['onTempMessageIdReplacedOverride'];

	// Context & lifecycle
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'];
	broadcastStreamLifecycle: (status: StreamLifecycleState) => void;
	streamActionInfo: StreamActionInfo;

	// Metrics
	stopInteractionMetricsTimeToFirstActionPart: ReturnType<typeof getMetricEndHelper>;
	stopInteractionMetricsTimeToFirstAnswerPart: ReturnType<typeof getMetricEndHelper>;
	stopInteractionMetricsTimeToActionCompletion: ReturnType<typeof getMetricEndHelper>;
	sendMessageLatencyMetrics: Metric;
	betweenChunksLatencyMetrics: Metric;
	cancelStreamingMetrics: () => void;

	// Passed from the bridge-api
	promptStreamHook: PromptStreamHook | undefined;
	openChat?: boolean;

	messageSources: Partial<MessageSource> | undefined;
	hasLoggedInitiatedEvent: boolean;
	clearMessageResearch: (args_0: { messageId: string }) => void;
	addImplicitResearchPlan: ChatStoreContainerProps['addImplicitResearchPlan'];
	resetHasReceivedAnswerPart: ChatStoreContainerProps['resetHasReceivedAnswerPart'];
	viewDeadAirBroker: ChatStoreContainerProps['viewDeadAirBroker'];
	minionAlias?: string;
	agentId?: string;
	creationContextParams?: CreationContextParams;
	storeUserMessages: boolean;

	// Reconnection support
	assistanceService: ChatStoreContainerProps['assistanceService'];
	abortController: AbortController;
	getIsChatMounted?: ChatStoreContainerProps['getIsChatMounted'];
}

/**
 * Mutable state object for tracking client-side implicit round detection
 * across stream messages. Passed by reference so the helper and caller
 * share the same counters.
 */
export type ResearchStreamState = {
	clientRoundCounter: number;
	hasAccumulatedAnswerContent: boolean;
};

export type ProcessResearchStreamMessageParams = {
	streamMessage: StreamMessage;
	tmpAssistantMessageId: string;
	conversationId: string;
	deepResearchEnabled: boolean | undefined;
	getMessages: ChatStoreContainerProps['getMessages'];
	setMessages: ChatStoreContainerProps['setMessages'];
	processStreamResearch: ChatStoreContainerProps['processStreamResearch'];
	addImplicitResearchPlan: ChatStoreContainerProps['addImplicitResearchPlan'];
	resetHasReceivedAnswerPart: ChatStoreContainerProps['resetHasReceivedAnswerPart'];
	/** Mutable state shared between calls; updated in-place */
	researchState: ResearchStreamState;
};

/**
 * Handles all research-store updates for a single stream message.
 *
 * Encapsulates:
 * - Implicit round detection for think-deeper (clientRoundCounter / hasAccumulatedAnswerContent)
 * - roundId override on research payload messages
 * - processStreamResearch dispatch for all applicable message types
 * - isStreamResearchPlan → processStreamResearch
 * - isStreamResearchStart → sets deepResearchInvoked flag (returned as boolean)
 * - isStreamAnswerPart → marks hasAccumulatedAnswerContent = true
 *
 * Returns `true` when deep research was detected as invoked (isStreamResearchStart),
 * so the caller can update its own tracking attributes.
 */
export const processResearchStreamMessage = ({
	streamMessage,
	tmpAssistantMessageId,
	conversationId,
	deepResearchEnabled,
	getMessages,
	setMessages,
	processStreamResearch,
	addImplicitResearchPlan,
	resetHasReceivedAnswerPart,
	researchState,
}: ProcessResearchStreamMessageParams): { deepResearchInvoked: boolean } => {
	let deepResearchInvoked = false;

	if (
		isStreamResearchStart(streamMessage) &&
		// The same message_template is used for thinking deeper + deep research
		// so we need to check the specific trace message to differentiate them
		streamMessage.message.content === 'Starting deep research execution'
	) {
		deepResearchInvoked = true;
	}

	// Implicit round detection for think deeper:
	// When answer content has accumulated (from ANSWER_PART messages) and new
	// research step information arrives, we know a new round is starting.
	// We retroactively create a research plan for the completed round using
	// the accumulated answer content, clear the streamed content, and increment
	// the round counter so subsequent steps are grouped under the new round.
	if (
		researchState.hasAccumulatedAnswerContent &&
		isStreamResearchPayload(streamMessage) &&
		fg('rovo_chat_td_implicit_rounds')
	) {
		// Read the accumulated answer content from the assistant message
		const accumulatedContent = getStoreAssistantMessageContent(
			getMessages(conversationId),
			tmpAssistantMessageId,
		);

		// Create a research plan for the completed round with the accumulated content
		addImplicitResearchPlan({
			messageId: tmpAssistantMessageId,
			content: accumulatedContent,
			roundId: researchState.clientRoundCounter,
		});

		// Increment round counter for the new incoming steps
		researchState.clientRoundCounter++;

		// Clear the assistant message content so the new round starts fresh
		const clearedMessages = clearStoreAssistantMessageContent(
			getMessages(conversationId),
			tmpAssistantMessageId,
		);
		setMessages(conversationId, clearedMessages);

		// Reset tracking flags
		researchState.hasAccumulatedAnswerContent = false;
		resetHasReceivedAnswerPart({ messageId: tmpAssistantMessageId });
	}

	if (
		isStreamResearchPayload(streamMessage) ||
		isStreamResponse(streamMessage) ||
		isStreamAnswerPart(streamMessage) ||
		isStreamReasoningPart(streamMessage) ||
		isStreamSkill(streamMessage) ||
		isStreamThinkDeeperTriggered(streamMessage)
	) {
		// When implicit rounds are active, override roundId on research step messages
		// so they are grouped under the current client-managed round
		if (
			researchState.clientRoundCounter > 0 &&
			isStreamResearchPayload(streamMessage) &&
			'message' in streamMessage &&
			'roundId' in (streamMessage as any).message &&
			fg('rovo_chat_td_implicit_rounds')
		) {
			(streamMessage as any).message.roundId = researchState.clientRoundCounter;
		}

		processStreamResearch({
			messageId: tmpAssistantMessageId,
			streamMessage,
			isDeepResearch: deepResearchEnabled ?? false,
		});
	}

	if (isStreamResearchPlan(streamMessage)) {
		processStreamResearch({
			messageId: tmpAssistantMessageId,
			streamMessage,
			isDeepResearch: deepResearchEnabled ?? false,
		});
	}

	// Track that answer content has accumulated so implicit round detection
	// can detect a new round starting when the next research payload arrives
	if (isStreamAnswerPart(streamMessage) && fg('rovo_chat_td_implicit_rounds')) {
		researchState.hasAccumulatedAnswerContent = true;
	}

	return { deepResearchInvoked };
};

export const processStream = async ({
	prompt,
	generator,
	productKey,
	answerIndex,
	tmpHumanMessageId,
	tmpAssistantMessageId,
	thinkDeeperEnabled,
	fastModeEnabled,
	deepResearchEnabled,
	webSearchEnabled,
	sendMessageExperienceNameCompiled,
	traceId,
	updateCurrentConversation,
	trackingAttributes,
	conversationId,
	getState,
	promptStreamHook,
	openChat,
	experienceTracker,
	setMessages,
	getMessages,
	processStreamResearch,
	processMessageStreamAction,
	processMessageStreamComplete,
	processMessageStreamActionClassification,
	containerOnTempMessageIdReplaced,
	updateResearchMessageId,
	sendAnalyticsEvent,
	chatContextStoreActions,
	stopInteractionMetricsTimeToFirstActionPart,
	stopInteractionMetricsTimeToFirstAnswerPart,
	stopInteractionMetricsTimeToActionCompletion,
	broadcastStreamLifecycle,
	sendMessageLatencyMetrics,
	betweenChunksLatencyMetrics,
	cancelStreamingMetrics,
	onTempMessageIdReplacedOverride,
	streamActionInfo,
	trackAIMAUEvent,
	messageSources,
	additionalAnalyticAttributes,
	hasLoggedInitiatedEvent,
	clearMessageResearch,
	addImplicitResearchPlan,
	resetHasReceivedAnswerPart,
	viewDeadAirBroker,
	minionAlias,
	agentId,
	creationContextParams,
	storeUserMessages,
	assistanceService,
	abortController,
	getIsChatMounted,
}: ProcessStreamParams) => {
	updateCurrentConversation({
		...(!fg('rovo_display_thinking_indicator') && {
			status: CHAT_STORE_STATUS.typingResponse,
		}),
		traces: [],
	});

	let hasReceivedFinalResponse = false;
	let hasReceivedFollowUp = false;
	let isLuminaAnswerGenerationType = false;
	let agentMessageId: string | undefined;
	let lastEventId: string | undefined;
	let isReconnectSupported = false;

	// Client-side implicit round tracking for think deeper.
	// Passed by reference into processResearchStreamMessage so state is shared.
	const researchState: ResearchStreamState = {
		clientRoundCounter: 0,
		hasAccumulatedAnswerContent: false,
	};

	const processStreamMarkdown = createStreamMarkdownProcessor();

	let latestStreamMessage: StreamMessage | undefined;

	const streamStats = getStreamStats();
	// Between-chunks inactivity watchdog (logs once per stall, re-arms on chunks)
	const inactivity = createBetweenChunksInactivityLogger({
		baseMs: 90_000,
		sendAnalyticsEvent: (event: AnalyticsEventPayload) => {
			sendAnalyticsEvent({
				...event,
				attributes: {
					...event.attributes,
					...streamStats.getStats(),
					isDeepResearchEnabled: deepResearchEnabled,
					isThinkDeeperEnabled: thinkDeeperEnabled,
					isFastModeEnabled: fastModeEnabled,
					isWebSearchEnabled: webSearchEnabled,
					isAIFCStreaminAction: streamActionInfo.isAction,
					conversationId,
					instrumentationId: trackingAttributes.instrumentationId,
					requestId: trackingAttributes.requestId,
					interactionSource: trackingAttributes.interactionSource,
					contentContext: chatContextStoreActions.getIsStagingArea() ? 'staging-area' : 'global',
					userAgent: navigator?.userAgent,
				},
			});
		},
	});

	// Dead air tracking - monitors gaps between meaningful stream messages
	const deadAirTracker = fg('rovo_chat_track_dead_air')
		? createDeadAirTracker({
				messageId: tmpAssistantMessageId,
				conversationId,
				traceId: traceId ?? '',
				productKey: productKey ?? '',
				deepResearchEnabled: deepResearchEnabled ?? false,
				thinkDeeperEnabled: thinkDeeperEnabled ?? false,
				fastModeEnabled: fastModeEnabled ?? false,
				webSearchEnabled: webSearchEnabled ?? false,
				isAIFC: streamActionInfo.isAction,
				sendAnalyticsEvent,
		  })
		: undefined;

	viewDeadAirBroker?.start({
		messageId: tmpAssistantMessageId,
		conversationId,
		traceId: traceId ?? '',
		productKey: productKey ?? '',
		reasoningMode: deepResearchEnabled
			? 'deep-research'
			: thinkDeeperEnabled
			? 'think-deeper'
			: fastModeEnabled
			? 'fast'
			: 'auto',
		isAIFCEnabled: streamActionInfo.isAction,
		isWebSearchEnabled: webSearchEnabled ?? false,
	});

	const startStreamingProcessingAt = performance.now();

	// Reset the stream response recording for a new stream session
	let canRecordStreamResponses = false;
	try {
		canRecordStreamResponses = Boolean(
			window.top?.__rovoChatResponseDebugger__ && fg('editor-4209_sse_stream_debug'),
		);
	} catch {
		canRecordStreamResponses = false;
	}

	if (canRecordStreamResponses) {
		resetStreamResponseRecording();
	}

	// Arm the inactivity watchdog
	inactivity?.arm();
	// Start the between chunks latency metrics
	betweenChunksLatencyMetrics.start({ startTime: startStreamingProcessingAt });

	/*****************************
	 *
	 * Stream processing START
	 *
	 *****************************/

	const streamToIterate = fg('rovo_chat_reconnectable_stream')
		? createChatReconnectableStream({
				generator,
				conversationId,
				abortController,
				assistanceService,
				sendAnalyticsEvent,
				experienceTracker,
				getMessageId: () => agentMessageId,
				canReconnect: () => !!(agentMessageId && isReconnectSupported),
				updateCurrentConversation,
				getIsChatMounted,
				analyticsAttributes: {
					isDeepResearch: deepResearchEnabled,
					thinkDeeperEnabled,
					fastModeEnabled,
				},
				onBeforeFirstReconnect: () => {
					const chat = getMessages(conversationId);
					const alreadySwapped = findStoreMessageIndex(chat, agentMessageId!) >= 0;

					if (!alreadySwapped) {
						setMessages(
							conversationId,
							updateStoreAssistantMessageId({
								chat,
								oldId: tmpAssistantMessageId,
								newId: agentMessageId!,
							}),
						);
						updateResearchMessageId({
							messageId: tmpAssistantMessageId,
							newMessageId: agentMessageId!,
						});
					}

					tmpAssistantMessageId = agentMessageId!;
				},
				onReconnected: () => {
					inactivity?.arm();
				},
				onFirstChunkAfterReconnect: () => {
					deadAirTracker?.resetTimestamp();
				},
		  })
		: generator;

	try {
		for await (const streamObj of streamToIterate) {
			// Track the last event ID for potential reconnection (legacy path)
			if (isSSEMessage(streamObj) && streamObj.id) {
				lastEventId = streamObj.id;
			}

			const streamMessage = isSSEMessage(streamObj) ? streamObj.data : streamObj;

			// Record the raw stream message for debugging purposes
			// This allows developers to copy/paste SSE responses for examples/tests
			if (canRecordStreamResponses) {
				recordStreamMessage(streamMessage);
			}

			latestStreamMessage = streamMessage;
			if (fg('rovo_chat_track_dead_air')) {
				deadAirTracker?.update(streamMessage);
			}
			streamStats.update(streamMessage);
			// any chunk = proof of life
			inactivity?.onChunk();

			// Note: We do NOT skip stream messages during SWITCHING.
			// Stream processing continues normally until FAST_MODE_ENABLED is received,
			// at which point research steps are cleared and status is set to SWITCHED.

			// Usually the first stream part
			if (isConversationChannelDataMessage(streamMessage)) {
				agentMessageId = streamMessage.assistant_message_id;

				updateCurrentConversation({
					name: streamMessage.name,
					lastMessageTimestamp: streamMessage.last_message_timestamp ?? new Date().toISOString(),
					timeCreated: streamMessage.time_created,
					...(streamMessage.scopes && { scopes: streamMessage.scopes }),
				});

				trackingAttributes.instrumentationId = agentMessageId;
				trackingAttributes.messageId = agentMessageId;
				trackingAttributes.invokedFrom =
					creationContextParams?.source === 'remix' ? creationContextParams?.source : undefined;

				trackAIMAUEvent({
					type: 'initiated',
					subjectId: messageSources?.interactionSource,
					proactive: false,
					userGenerated: true,
					timestamp: streamMessage.last_message_timestamp,
					singleInstrumentationID: agentMessageId,
					deepResearchEnabled: deepResearchEnabled ?? false,
					thinkDeeperEnabled: thinkDeeperEnabled ?? false,
					fastModeEnabled: fastModeEnabled ?? false,
					webSearchEnabled: webSearchEnabled ?? false,
					pageContext: trackingAttributes.pageContext,
					invokedFrom: trackingAttributes.invokedFrom,
					attributes: {
						...(additionalAnalyticAttributes && additionalAnalyticAttributes),
					},
				});
				hasLoggedInitiatedEvent = true;
			} else if (fg('rovo_display_thinking_indicator')) {
				const conversation = getState().conversations[getState().currentConversationId];
				if (conversation) {
					if (conversation.status !== CHAT_STORE_STATUS.typingResponse) {
						updateCurrentConversation({
							status: CHAT_STORE_STATUS.typingResponse,
						});
					}
				}
			}

			if (isStreamReconnectSupportPart(streamMessage)) {
				isReconnectSupported = true;
			}

			if (isStreamAnswerGenerationType(streamMessage) && fg('rovo_chat_enable_lumina')) {
				isLuminaAnswerGenerationType = streamMessage.message.answer_generation_type === 'LUMINA';
				setMessages(
					conversationId,
					updateStoreAssistantMessageAnswerGenerationType(
						getMessages(conversationId),
						tmpAssistantMessageId,
						streamMessage.message.answer_generation_type,
					),
				);
			}

			{
				const { deepResearchInvoked } = processResearchStreamMessage({
					streamMessage,
					tmpAssistantMessageId,
					conversationId,
					deepResearchEnabled,
					getMessages,
					setMessages,
					processStreamResearch,
					addImplicitResearchPlan,
					resetHasReceivedAnswerPart,
					researchState,
				});
				if (deepResearchInvoked) {
					trackingAttributes.deepResearchInvoked = true;
				}
			}

			// This is the first moment we have the network request id for E2E event tracking
			// Store requestId on human message for E2E event tracking
			if (!trackingAttributes.requestId && 'metadata' in streamMessage) {
				trackingAttributes.requestId = streamMessage.metadata?.request_id;
			}

			// Handle MEMORY_UPDATE trace messages - create a persistent StoreMemoryUpdate message
			if (
				isStreamMemoryUpdate(streamMessage) &&
				FeatureGates.getExperimentValue('memory_management_ux', 'isEnabled', false)
			) {
				const originalContent = streamMessage.message.content;
				const isMemoryTraceContentEmpty = !originalContent;
				let memoryContent = originalContent;
				let isMemoryContentAfterParsingEmpty = false;

				// Parse memory content: remove "Update memory:" prefix and trim
				if (memoryContent) {
					memoryContent = memoryContent.replace(/^update\s+memory:\s*/i, '').trim();
					isMemoryContentAfterParsingEmpty = !memoryContent;
				}

				const memoryUpdateMessage = {
					type: 'MEMORY_UPDATE' as const,
					content: memoryContent || undefined, // Use undefined instead of empty string
				};

				// Track memory update parsing events
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'parsed',
					actionSubject: 'memoryUpdate',
					attributes: {
						isMemoryTraceContentEmpty,
						isMemoryContentAfterParsingEmpty,
						conversationId,
					},
				});

				setMessages(
					conversationId,
					addMemorytoLatestAssistantMessage({
						chat: getMessages(conversationId),
						answerId: tmpAssistantMessageId,
						memory: memoryUpdateMessage,
						useIndex: answerIndex,
					}),
				);
			}

			// Usually the second stream part, before anything else occurs
			if (isStreamTrace(streamMessage)) {
				if (!deepResearchEnabled) {
					// we do not want traces that are not marked for deep research to be accumulated when starting research,
					// the ones that are marked are processed above in processResearchStreamMessage
					updateCurrentConversation({
						traces: dedupeTraceMessagesWithLatest([
							...getConversationTraces(getState(), conversationId),
							getTraceFromStreamMessage(streamMessage),
						]),
					});
				}

				promptStreamHook?.onTraceMessage?.(streamMessage, tmpAssistantMessageId, openChat);

				setMessages(
					conversationId,
					addRequestIdToLatestHumanMessage(
						getMessages(conversationId),
						trackingAttributes.requestId,
					),
				);
			}

			// Handle Answer Now fast mode traces for extended thinking
			// Answer Now feature - handle fast mode traces
			if (fg('rovo_chat_enable_ans_now')) {
				if (isStreamFastModeResponseAvailable(streamMessage) && agentMessageId) {
					// Fast mode response is available - show Answer Now button
					// Store both IDs in AVAILABLE status:
					// - tmpMessageId: for store operations (finding/updating the message)
					// - remoteMessageId: for the Answer Now API call
					setMessages(
						conversationId,
						updateStoreAssistantMessageAnswerNowStatus(
							getMessages(conversationId),
							tmpAssistantMessageId,
							{
								type: AnswerNowStatusType.AVAILABLE,
								tmpMessageId: tmpAssistantMessageId,
								remoteMessageId: agentMessageId,
							},
						),
					);
				}

				if (isStreamFastModeEnabled(streamMessage)) {
					// Fast mode has been enabled:
					// 1. Set status to SWITCHED (hides button)
					// 2. Clear research steps for this message
					setMessages(
						conversationId,
						updateStoreAssistantMessageAnswerNowStatus(
							getMessages(conversationId),
							tmpAssistantMessageId,
							{ type: AnswerNowStatusType.SWITCHED },
						),
					);
					// Clear research steps when switching to fast mode
					clearMessageResearch({ messageId: tmpAssistantMessageId });
				}

				if (isStreamRemoveAnswerNow(streamMessage)) {
					clearAnswerNowStatusForMessageIfSet(
						conversationId,
						tmpAssistantMessageId,
						getMessages,
						setMessages,
					);
				}
			}

			// isStreamResearchPlan is handled by processResearchStreamMessage (called earlier in this loop)

			let commonExperienceAttributes: Record<string, any> = {
				llmDebugLogsEnabled: storeUserMessages,
			};
			if (isStreamActionClassification(streamMessage)) {
				const actionKey = streamMessage.message.action_key;
				const actualContentType = getAIFCContentType({ key: actionKey });
				commonExperienceAttributes = {
					...commonExperienceAttributes,
					actionKey,
					contentType: actualContentType,
					isStagingArea: chatContextStoreActions.getIsStagingArea(),
					product: productKey,
				};

				if (trackingAttributes.forcedContentType) {
					sendAnalyticsEvent({
						eventType: 'track',
						action: 'compared',
						actionSubject: 'forcedContentType',
						actionSubjectId: 'contentTypeMatch',
						attributes: {
							forcedContentType: trackingAttributes.forcedContentType,
							actualContentType,
							matched: actualContentType.endsWith(`-${trackingAttributes.forcedContentType}`),
							actionKey,
						},
					});
				}

				const isUpdateAction = isUpdatableAction({ key: actionKey });
				if (isUpdateAction) {
					experienceTracker.start({
						name: ExperienceName.AIFC_UPDATE_CONTENT,
						id: conversationId,
						collect: createAifcExperienceCollect(),
						attributes: commonExperienceAttributes,
					});
					// Start AIFC_ALL_DATA_HANDLED - this replaces all explicit AIFC_UPDATE_CONTENT succeed/fail/abort calls
					// Product AIFC integration is responsible for succeeding this experience.
					experienceTracker.start({
						name: ExperienceName.AIFC_ALL_DATA_HANDLED,
						id: conversationId,
						attributes: {
							...commonExperienceAttributes,
							parentExperienceId: ExperienceName.AIFC_UPDATE_CONTENT,
						},
					});
					experienceTracker.start({
						name: ExperienceName.AIFC_MESSAGE,
						id: conversationId,
						attributes: {
							...commonExperienceAttributes,
							parentExperienceId: ExperienceName.AIFC_UPDATE_CONTENT,
						},
					});
					// Start AIFC_INITIAL_DATA_RECEIVED with timeout to detect if content fails to start streaming within 90s.
					// If timeout occurs, this experience will fail and AIFC_UPDATE_CONTENT will fail (due to collect function).
					// Product AIFC integration is responsible for succeeding this experience.
					// Note: Deep research is excluded as it could take longer so timing out is less sensible.
					if (!deepResearchEnabled && fg('aifc-experience-timeout')) {
						experienceTracker.start({
							name: ExperienceName.AIFC_INITIAL_DATA_RECEIVED,
							id: `${conversationId}-initial`,
							timeout: 90_000, // 90 second timeout
							attributes: {
								...commonExperienceAttributes,
								parentExperienceId: ExperienceName.AIFC_UPDATE_CONTENT,
							},
						});
					}
				}

				const isCreateAction = isCreatableAction({ key: actionKey });
				if (isCreateAction) {
					const isStagingArea = chatContextStoreActions.getIsStagingArea();
					const humanMessageCount = getMessages(conversationId).filter(
						(message) => message.type === 'HUMAN',
					).length;
					// initial prompt from staging area has its own AIFC_GENERATE_CONTENT experience - dont start this one
					if (!isStagingArea || humanMessageCount > 1) {
						experienceTracker.start({
							name: ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT,
							id: conversationId,
							collect: createAifcCreateInChatExperienceCollect(),
							attributes: commonExperienceAttributes,
						});

						experienceTracker.start({
							name: ExperienceName.AIFC_MESSAGE,
							id: conversationId,
							attributes: {
								...commonExperienceAttributes,
								parentExperienceId: ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT,
							},
						});
						experienceTracker.start({
							name: ExperienceName.PREVIEW_CARD_RENDER,
							id: conversationId,
							timeout: deepResearchEnabled ? undefined : 60_000, // 60 second timeout
							attributes: {
								...commonExperienceAttributes,
								parentExperienceId: ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT,
							},
						});
						// Product AIFC integration is responsible for succeeding this experience.
						experienceTracker.start({
							name: ExperienceName.AIFC_ALL_DATA_HANDLED,
							id: conversationId,
							attributes: {
								...commonExperienceAttributes,
								parentExperienceId: ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT,
							},
						});
					} else {
						// update AIFC_GENERATE_CONTENT now that intent detection is complete. This experience starts as the user sends the message on the initial prompt screen
						experienceTracker.updateAttributes({
							name: ExperienceName.AIFC_GENERATE_CONTENT,
							attributes: commonExperienceAttributes,
						});

						experienceTracker.updateAttributes({
							name: ExperienceName.AIFC_INITIAL_DATA_RECEIVED,
							attributes: commonExperienceAttributes,
						});
					}
				}
			}

			if (
				(isStreamActionPart(streamMessage) || isStreamActionPrepare(streamMessage)) &&
				fg('aifc_create_enabled')
			) {
				// Update action tracking info
				if (!streamActionInfo.isAction) {
					streamActionInfo.isAction = true;
					streamActionInfo.actionKey = streamMessage.message.key;
				}

				// track attribute for action prepare
				if (isStreamActionPrepare(streamMessage) && expValEquals('cc-maui-experiment', 'isEnabled', true)) {
					const actionData = streamMessage.message.data as { dynamicUiSubtype?: string } | undefined;
					experienceTracker.updateAttributes({
						name: ExperienceName.AIFC_MESSAGE,
						attributes: { dynamicUiSubtype: actionData?.dynamicUiSubtype },
					});
				}

				if (isStreamActionPart(streamMessage)) {
					stopInteractionMetricsTimeToFirstActionPart('stop', {
						actionType: 'action-part',
						conversationId,
						actionPartKey: getActionPartKeyForMetrics(
							streamMessage.message.key,
							creationContextParams?.source,
						),
					});
				}

				if (fg('aifc_set_accumulated_data_on_message')) {
					const actionKey = streamMessage.message.key;
					const lastAction = findLastAssistantAction(
						getMessages(conversationId),
						(action) => action.key === actionKey,
					);

					if (lastAction && isAccumulatedAction(actionKey)) {
						const isAccumulatedActionType = getAccumulatedActionTypeGuard(actionKey);
						if (isAccumulatedActionType(streamMessage.message.data)) {
							const primaryAccumulatedData = getPrimaryAccumulatedValue(lastAction);
							if (primaryAccumulatedData?.value) {
								streamMessage.message.data.previousAccumulatedData = primaryAccumulatedData.value;
							}
						}
					}
				}

				processMessageStreamAction?.(
					tmpAssistantMessageId,
					streamMessage,
					traceId,
					trackingAttributes.requestId,
					fg('aifc_header_image_pseudo_title_enabled') ? prompt : undefined,
					{
						singleInstrumentationId: trackingAttributes.instrumentationId,
					},
				);

				promptStreamHook?.onActionPart?.(
					streamMessage,
					tmpAssistantMessageId,
					conversationId,
					openChat,
				);

				const updatedMessages = addActionPart({
					chat: getMessages(conversationId),
					messageId: tmpAssistantMessageId,
					actionPart: streamMessage,
					...(fg('aifc_set_accumulated_data_on_message')
						? { actionUpdaterOverride: customActionUpdater }
						: {}),
				});

				if (isStreamActionPrepare(streamMessage)) {
					const actionMessage = streamMessage.message;
					setMessages(conversationId, [
						...updatedMessages,
						{ type: 'ACTION_PREPARE', action: actionMessage },
					]);
				} else {
					setMessages(conversationId, updatedMessages);
				}
			}

			const isAIFCEnabled = fg('aifc_create_enabled');
			const canHandleStreamActionClassification = fg(
				'platform_editor_ai_promptversion_in_analytics',
			);
			if (
				isStreamActionClassification(streamMessage) &&
				isAIFCEnabled &&
				canHandleStreamActionClassification &&
				streamMessage.message.metadata
			) {
				// Send ACTION_CLASSIFICATION metadata to the Editor Rovo bridge
				processMessageStreamActionClassification?.(
					tmpAssistantMessageId,
					streamMessage,
					traceId,
					trackingAttributes.requestId,
					{
						singleInstrumentationId: trackingAttributes.instrumentationId,
					},
				);
			}

			if (isStreamActionComplete(streamMessage) && fg('aifc_create_enabled')) {
				processMessageStreamComplete?.(
					tmpAssistantMessageId,
					streamMessage,
					traceId,
					trackingAttributes.requestId,
					{
						singleInstrumentationId: trackingAttributes.instrumentationId,
					},
				);
				const actionMessage = streamMessage.message;
				setMessages(conversationId, [
					...getMessages(conversationId),
					{ type: 'ACTION_COMPLETE', action: actionMessage },
				]);
				experienceTracker.succeed({ name: ExperienceName.AIFC_MESSAGE });
			}

			if (isStreamAnswerPart(streamMessage)) {
				stopInteractionMetricsTimeToFirstAnswerPart('stop', {
					answerType: 'stream-answer-part',
					conversationId,
				});

				// Clear Answer Now status when ANSWER_PART is received
				// This handles the case where the stream starts answering without user clicking Answer Now
				if (fg('rovo_chat_enable_ans_now')) {
					clearAnswerNowStatusForMessageIfSet(
						conversationId,
						tmpAssistantMessageId,
						getMessages,
						setMessages,
					);
				}

				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

				let processedStreamMessage = streamMessage;
				let processedStreamResult = null;

				if (!isLuminaAnswerGenerationType && fg('rovo_enable_component_streaming')) {
					processedStreamResult = processStreamMarkdown(processedStreamMessage);
					processedStreamMessage = processedStreamResult.processedStreamMessage;
				}

				if (!isLuminaAnswerGenerationType) {
					promptStreamHook?.onAnswerPart?.(
						processedStreamMessage,
						tmpAssistantMessageId,
						conversationId,
					);
				}
				if (
					processedStreamResult &&
					processedStreamResult.markdownData &&
					fg('rovo_enable_component_streaming')
				) {
					setMessages(
						conversationId,
						addAnswerPartWithMarkdownData({
							chat: getMessages(conversationId),
							answerId: tmpAssistantMessageId,
							answerPart: processedStreamMessage,
							markdownData: processedStreamResult.markdownData,
							useIndex: answerIndex,
						}),
					);
				} else {
					setMessages(
						conversationId,
						addAnswerPart({
							chat: getMessages(conversationId),
							answerId: tmpAssistantMessageId,
							answerPart: streamMessage,
							useIndex: answerIndex,
						}),
					);
				}

				// hasAccumulatedAnswerContent tracking is handled by processResearchStreamMessage
			}

			/**********************************************************
			 *
			 * FINAL_RESPONSE processing
			 *
			 **********************************************************/
			if (isStreamResponse(streamMessage)) {
				hasReceivedFinalResponse = true;
				const humanMessageId = streamMessage?.message?.human_message?.id.toString();
				const agentMessageId = streamMessage?.message?.message?.id.toString();
				const pluginInvocations = streamMessage?.message?.message?.plugin_invocations;
				const minionOrchestrationOutputs = streamMessage?.message?.message?.minion_outputs;
				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
				const { processedStreamMessage, markdownData } = !isLuminaAnswerGenerationType
					? processStreamResponseMarkdown({
							streamMessage,
							messageId: agentMessageId,
							conversationId,
							sendAnalyticsEvent,
					  })
					: { processedStreamMessage: streamMessage, markdownData: {} };

				// Finish view dead air tracking before rendering final response
				// This ensures the last tracked interval is up to FINAL_RESPONSE arrival,
				// and any DOM updates from replaceAnswerData are not tracked
				viewDeadAirBroker?.finish('completed');

				promptStreamHook?.onFinalResponse?.(processedStreamMessage, tmpAssistantMessageId);
				setMessages(
					conversationId,
					replaceAnswerData({
						chat: getMessages(conversationId),
						answerId: tmpAssistantMessageId,
						content: processedStreamMessage,
						useIndex: answerIndex,
						humanMessageId,
						markdownData: Object.keys(markdownData).length > 0 ? markdownData : undefined,
						onMessageIdChanged: onTempMessageIdReplacedOverride || containerOnTempMessageIdReplaced,
					}),
				);

				if (humanMessageId) {
					setMessages(
						conversationId,
						updateHumanMessageId({
							chat: getMessages(conversationId),
							oldId: tmpHumanMessageId,
							newId: humanMessageId,
						}),
					);
				}

				if (fg('aifc_create_enabled')) {
					const allMessages = getMessages(conversationId);
					const isValidAction = handleStagingAreaActionValidation(
						allMessages,
						conversationId,
						traceId,
						chatContextStoreActions,
						experienceTracker,
						getMessages,
						setMessages,
						sendMessageLatencyMetrics,
						cancelStreamingMetrics,
						commonExperienceAttributes,
						productKey,
						minionOrchestrationOutputs,
					);

					if (!isValidAction) {
						return { hasReceivedFinalResponse, hasLoggedInitiatedEvent };
					}

					const finalActions = processedStreamMessage.message.message.actions || [];
					if (finalActions.length > 0) {
						stopInteractionMetricsTimeToActionCompletion('stop', {
							conversationId,
							actionType: 'final-actions',
							actionCount: finalActions.length,
							actionKeys: finalActions.map((a) => a.key),
							actionPartKey: getActionPartKeyForMetrics(
								streamActionInfo.actionKey,
								creationContextParams?.source,
							),
						});
					} else {
						stopInteractionMetricsTimeToActionCompletion('cancel');
					}
				}

				// Update tracking attributes
				if (!hasLoggedInitiatedEvent) {
					trackingAttributes.messageId = agentMessageId;
					trackingAttributes.instrumentationId = agentMessageId;
				}
				trackingAttributes.humanMessageId = humanMessageId;
				trackingAttributes.hasSources = !!streamMessage?.message?.message?.sources?.length;
				trackingAttributes.citedProductList = constructCitedProductList(
					streamMessage?.message?.message?.sources,
				);
				trackingAttributes.sourceProducts = constructSourceProducts(
					streamMessage?.message?.message?.sources,
				);

				trackingAttributes.messageActions = streamMessage.message.message?.actions?.map(
					(action) => action.key,
				);

				if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
					trackingAttributes.messageDynamicUIData = streamMessage.message.message?.actions?.map(
						(action) => {
							const actionData = action.data as { dynamicUiSubtype?: string } | undefined;
							return {
								subtype: actionData?.dynamicUiSubtype,
							};
						},
					);
				}

				trackingAttributes.invokedFrom = getInvokedFrom({
					messageActionKey: trackingAttributes.messageActions?.[0],
					messageActionData: streamMessage.message.message?.actions?.[0]?.data,
				});
				trackingAttributes.scenarioId = streamMessage.message.message?.scenario?.id;

				if (fg('rovo_chat_analytics_source_extended')) {
					trackingAttributes.sources = streamMessage?.message?.message?.sources
						?.map((x) => x.ari)
						.filter(Boolean);
					trackingAttributes.usesJsm = false;
				}

				if (pluginInvocations) {
					pluginInvocations.forEach((pluginInvocation) => {
						if (pluginInvocation.plugin_name) {
							trackingAttributes.pluginsInvoked.add(pluginInvocation.plugin_name);
						}
						if (
							pluginInvocation.plugin_output &&
							containsServiceDeskSource(pluginInvocation.plugin_output) &&
							fg('rovo_chat_analytics_source_extended')
						) {
							trackingAttributes.usesJsm = true;
						}
					});
				}

				const flatMinionOutputs = minionOrchestrationOutputs?.flat() ?? [];

				trackingAttributes.executedStratusSkill =
					flatMinionOutputs.some((output) => output.skill_type === 'STRATUS') ?? false;

				// Analytics: Verify if minion alias matches minion id
				fg('studio_fe_slos') &&
					minionVerificationAnalytics({
						minionAlias,
						...(fg('fix_minion_verification_event') && { agentId }),
						minionsOutput: flatMinionOutputs,
						sendAnalyticsEvent,
					});

				flatMinionOutputs.forEach((output) => {
					trackingAttributes.pluginsInvoked.add(output.minion_id);
					// NOTE: statsig does not allow tracking arrays of objects so this attribute is only useful in databricks
					trackingAttributes.skillsInvoked.push({
						minionId: output.minion_id,
						skillName: output.skill_alias,
						skillType: output.skill_type,
						invocationType: output.directly_invoked ? 'explicit' : 'implicit',
						internalTools:
							output.internal_tools?.map((tool) => ({
								name: tool.name,
								isConsequential: tool.is_consequential,
							})) ?? [],
					});

					if (
						output.minion_output &&
						containsServiceDeskSource(output.minion_output) &&
						fg('rovo_chat_analytics_source_extended')
					) {
						trackingAttributes.usesJsm = true;
					}
				});

				updateResearchMessageId({
					messageId: tmpAssistantMessageId,
					newMessageId: agentMessageId,
				});
			}

			if (isStreamDynamicLayoutSubAgent(streamMessage) && fg('rovo_chat_enable_lumina')) {
				setMessages(
					conversationId,
					addSubAgentMessageToStoreAssistantMessage(
						getMessages(conversationId),
						tmpAssistantMessageId,
						streamMessage,
					),
				);
			}

			if (isFollowUpMessage(streamMessage)) {
				hasReceivedFollowUp = true;
				const messages = getMessages(conversationId);
				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

				const augmentedFollowUp = appendCreatePageFollowUpIfNeeded(
					messages,
					streamMessage,
					productKey,
				);

				setMessages(conversationId, [
					...messages,
					{
						type: 'FOLLOW_UP',
						content: augmentedFollowUp,
						id: randomIntFromInterval(0, 1000000),
						agentMessageId: getLastAgentMessageID(messages),
						humanMessageId: trackingAttributes.humanMessageId,
					},
				]);
			}

			if (isStreamActionError(streamMessage)) {
				sendMessageLatencyMetrics.cancel();
				cancelStreamingMetrics();
				promptStreamHook?.onError?.(streamMessage, tmpAssistantMessageId);
				broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

				const actionError = new StreamErrorException(
					streamMessage.message.data?.message || 'Action execution failed',
					{
						detail: {
							message: {
								content: streamMessage?.message?.data?.message || 'Action execution failed',
								message_template: streamMessage?.message?.key,
							},
							metadata: {
								...streamMessage.metadata,
								isStagingArea: chatContextStoreActions.getIsStagingArea(),
								productKey,
							},
						},
						traceId: traceId,
					},
				);

				experienceTracker.fail({
					name: sendMessageExperienceNameCompiled,
					error: actionError,
					attributes: {
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						isDeepResearchEnabled: deepResearchEnabled,
						isThinkDeeperEnabled: thinkDeeperEnabled,
						isFastModeEnabled: fastModeEnabled,
						isWebSearchEnabled: webSearchEnabled,
						llmDebugLogsEnabled: storeUserMessages,
						productKey,
						isStreamAction: streamActionInfo.isAction,
						actionKey: streamActionInfo.actionKey,
					},
				});

				experienceTracker.fail({
					name: ExperienceName.AIFC_MESSAGE,
					error: actionError,
					attributes: {
						productKey,
						isStreamAction: streamActionInfo.isAction,
						actionKey: streamActionInfo.actionKey,
						llmDebugLogsEnabled: storeUserMessages,
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						latestStreamMessageMillisOffset:
							latestStreamMessage && 'millisOffset' in latestStreamMessage
								? latestStreamMessage.millisOffset
								: undefined,
						latestStreamMessageType: latestStreamMessage?.type,
					},
				});

				if (streamActionInfo.isAction && !deepResearchEnabled && fg('aifc-experience-timeout')) {
					for (const name of [ExperienceName.AIFC_INITIAL_DATA_RECEIVED, ExperienceName.AIFC_ALL_DATA_HANDLED]) {
						experienceTracker.fail({ name, error: actionError, attributes: { productKey, actionKey: streamActionInfo.actionKey } });
					}
				}

				throw actionError;
			}

			if (isStreamError(streamMessage)) {
				const { message, metadata, millisOffset, type } = streamMessage;

				// AUP violations are still considered successful
				if (isStreamAUPError(streamMessage)) {
					experienceTracker.succeed({
						name: sendMessageExperienceNameCompiled,
					});
					experienceTracker.succeed({ name: ExperienceName.AIFC_MESSAGE });
				}

				sendMessageLatencyMetrics.cancel();
				betweenChunksLatencyMetrics.cancel();
				if (fg('rovo_chat_track_dead_air')) {
					deadAirTracker?.cancel();
				}
				viewDeadAirBroker?.finish('errored');
				cancelStreamingMetrics();

				promptStreamHook?.onError?.(streamMessage, tmpAssistantMessageId);
				broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

				throw new StreamErrorException(message?.content || 'Unknown', {
					detail: {
						message,
						metadata,
						millisOffset,
						type,
					},
					traceId: traceId,
				});
			}
		}
	} catch (error: unknown) {
		/* for await catch block */
		const err = error instanceof Error ? error : new Error(String(error));

		if (err instanceof StreamErrorException) {
			throw err;
		}

		// this is for any errors that happening during processing the stream
		// e.g. BE terminates streaming suddenly
		sendMessageLatencyMetrics.cancel();
		betweenChunksLatencyMetrics.cancel();
		if (fg('rovo_chat_track_dead_air')) {
			deadAirTracker?.cancel();
		}
		viewDeadAirBroker?.finish('aborted');

		cancelStreamingMetrics();

		if (isClientSideStreamingError(err)) {
			const reconnectionPossible = agentMessageId && isReconnectSupported;

			if (reconnectionPossible && !fg('rovo_chat_reconnectable_stream')) {
				setMessages(
					conversationId,
					updateStoreAssistantMessageId({
						chat: getMessages(conversationId),
						oldId: tmpAssistantMessageId,
						newId: agentMessageId!,
					}),
				);

				updateResearchMessageId({
					messageId: tmpAssistantMessageId,
					newMessageId: agentMessageId!,
				});

				throw new StreamDisconnectedError(err.message ?? 'Stream client side error', {
					agentMessageId: agentMessageId!,
					conversationId,
					answerIndex,
					lastEventId,
					originalError: err,
					traceId,
					detail: {
						millisOffset:
							latestStreamMessage && 'millisOffset' in latestStreamMessage
								? latestStreamMessage.millisOffset
								: undefined,
						type: latestStreamMessage?.type ?? '',
					},
				});
			}

			sendAnalyticsEvent({
				eventType: 'track',
				action: 'failed',
				actionSubject: 'rovoChatStream',
				attributes: {
					...streamStats.getStats(),
					isDeepResearchEnabled: deepResearchEnabled,
					isThinkDeeperEnabled: thinkDeeperEnabled,
					isFastModeEnabled: fastModeEnabled,
					isWebSearchEnabled: webSearchEnabled,
					isAIFCStreaminAction: streamActionInfo.isAction,
					isReconnectable: reconnectionPossible,
					conversationId,
					instrumentationId: trackingAttributes.instrumentationId,
					requestId: trackingAttributes.requestId,
					interactionSource: trackingAttributes.interactionSource,
					contentContext: chatContextStoreActions.getIsStagingArea() ? 'staging-area' : 'global',
					userAgent: navigator?.userAgent,
					error: err.message,
				},
			});
		}

		broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

		// When reconnect attempt fails
		if (streamActionInfo.isAction && !deepResearchEnabled && fg('aifc-experience-timeout')) {
			for (const name of [ExperienceName.AIFC_INITIAL_DATA_RECEIVED, ExperienceName.AIFC_ALL_DATA_HANDLED]) {
				experienceTracker.fail({ name, error: err, attributes: { productKey } });
			}
		}

		throw new StreamGeneratorError(
			err.message ?? 'Stream generator error',
			{
				traceId: traceId,
				detail: {
					millisOffset:
						latestStreamMessage && 'millisOffset' in latestStreamMessage
							? latestStreamMessage.millisOffset
							: undefined,
					type: latestStreamMessage?.type ?? '',
				},
			},
			err,
		);
	} finally {
		inactivity?.stop();
		betweenChunksLatencyMetrics.stop({
			stopTime: startStreamingProcessingAt + streamStats.getStats().averageGapMs,
		});

		if (fg('rovo_chat_track_dead_air')) {
			deadAirTracker?.complete();
		}
		viewDeadAirBroker?.finish('completed');
	}

	/**********************************************************
	 *
	 * Stream processing END
	 *
	 **********************************************************/

	if (!hasReceivedFollowUp && hasReceivedFinalResponse) {
		const messages = getMessages(conversationId);
		if (shouldAppendCreatePageFollowUp(messages, productKey)) {
			setMessages(conversationId, [
				...messages,
				{
					type: 'FOLLOW_UP',
					content: createSyntheticCreatePageFollowUp(),
					id: randomIntFromInterval(0, 1000000),
					agentMessageId: getLastAgentMessageID(messages),
					humanMessageId: trackingAttributes.humanMessageId,
				},
			]);
		}
	}

	updateCurrentConversation({
		sourceAgentId: getSourceAgentId(
			getMessages(conversationId),
			getConversation(getState(), conversationId),
		),
	});

	return { hasReceivedFinalResponse, hasLoggedInitiatedEvent };
};

type StreamActionInfo = {
	isAction: boolean;
	actionKey?: string;
};

export const sendMessageActionProvider: (params: SendMessageParams) => ChatStoreAction =
	({
		prompt,
		/*
	As of RAGE-60 the default agent is no longer required to be sent to the backend, however
	it is still required for other local logic
	TODO: https://product-fabric.atlassian.net/browse/RAGE-418
	*/
		agent = DEFAULT_AGENT,
		agentId: agentIdParam,
		conversationId: conversationIdParam,
		storeUserMessages,
		messageSources,
		files,
		deepResearchEnabled,
		thinkDeeperEnabled,
		alphaExtendedThinkingMode,
		fastModeEnabled,
		taskModeEnabled,
		webSearchEnabled,
		appFilters,
		addCurrentPageAsContextUserPreference,
		productKey,
		source,
		promptStreamHook,
		creationContextParams,
		additionalAnalyticAttributes,
		contentContext,
		minionAlias,
		onTempMessageIdReplacedOverride,
		contentId,
		openChat,
		setConversationData,
		createConversationOptimistically,
		addHumanMessage,
		addAssistantMessage,
		clearFollowUpMessages,
		setSelectedConversation,
		reconnectToInProgressStream,
	}) =>
	async (
		{ dispatch, getState, setState },
		{
			assistanceService,
			experienceTracker,
			sendAnalyticsEvent,
			chatContextStoreActions,
			trackAIMAUEvent,
			getMessages,
			setMessages,
			processStreamResearch,
			markIncompleteResearchesErrored,
			updateResearchMessageId,
			clearMessageResearch,
			addImplicitResearchPlan,
			resetHasReceivedAnswerPart,
			getChatInitiator,
			processMessageStreamAction,
			processMessageStreamComplete,
			processMessageStreamActionClassification,
			broadcastStreamStatus,
			onNewConversationStartingWithSendMessage,
			onTempMessageIdReplaced: containerOnTempMessageIdReplaced,
			browserContextSanitizer,
			viewDeadAirBroker,
			getIsChatMounted,
		},
	) => {
		const agentId = agentIdParam ?? agent.id;
		const sendMessageExperienceNameCompiled = sendMessageExperienceName({
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			isCustomRovoAgent: isRovoCustomAgent(agentId),
		});

		const { messageType } = getOptimisticMessageTypes(deepResearchEnabled, webSearchEnabled);

		setLastMessageSentAt(Date.now());

		let pageContext: string | undefined;

		const sendMessageFn = async ({ conversationId }: { conversationId: string }) => {
			const currentConversation = getConversation(getState(), conversationId);

			if (currentConversation?.storeAbortController) {
				// Silently aborting if there is another operation in progress
				return;
			}

			const tmpHumanMessageId = generateId();

			const tmpAssistantMessageId = generateTmpMessageId(conversationId);

			let generator: AsyncGenerator<StreamMessage | ParsedSSEMessage<StreamMessage>> | undefined;
			let responseData: FetchStreamReturn<StreamMessage>['responseData'] | undefined;
			let hasLoggedInitiatedEvent = false;

			const isFirstPrompt = isEmptyConversation(conversationId);

			const contentContextFromStore = chatContextStoreActions.getIsStagingArea()
				? 'staging-area'
				: 'global';
			const contentContextValue = contentContext ?? contentContextFromStore;

			let trackingAttributes: SendMessageTrackingAttributes = getInitialTrackingAttributes(
				agentId,
				tmpAssistantMessageId,
				deepResearchEnabled,
				thinkDeeperEnabled,
				fastModeEnabled,
				webSearchEnabled,
				messageSources,
				isFirstPrompt,
				prompt,
				files,
				appFilters,
				{
					templateId: creationContextParams?.templateInput?.templateId,
					templateType: creationContextParams?.templateInput?.templateType,
				},
				creationContextParams?.forcedContentType,
			);

			// Handle early creation of conversation data when starting from an empty state
			if (isFirstPrompt) {
				// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
				conversationId = uuidv4();
				onNewConversationStartingWithSendMessage(conversationId, {
					deepResearchEnabled,
					thinkDeeperEnabled,
					alphaExtendedThinkingMode,
					fastModeEnabled,
					...(taskModeEnabled ? { planModeEnabled: taskModeEnabled } : {}),
					webSearchEnabled,
					useCurrentPageContext: addCurrentPageAsContextUserPreference,
					appFilters,
				});
				dispatch(
					createConversationOptimistically({
						id: conversationId,
						status: CHAT_STORE_STATUS.awaitingResponse,
						sourceAgentId: currentConversation?.sourceAgentId ?? agentId,
						lastMessageType: messageType,
						lastAgentMessageStatus: 'IN_PROGRESS',
						...(!agent.is_default && fg('rovo_chat_chat_history_refresh')
							? {
									agent: {
										agent_id: agent.id,
										agent_named_id: agent.external_config_reference ?? agent.named_id,
										agent_name: agent.name,
										creator_type: agent.creator_type,
										identity_account_id: agent.identity_account_id ?? undefined,
										is_default: agent.is_default,
										forge_url: agent.icon ?? undefined,
									},
							  }
							: {}),
					}),
				);
			}

			const {
				betweenChunksLatencyMetrics,
				sendMessageLatencyMetrics,
				stopInteractionMetricsTimeToFirstAnswerPart,
				stopInteractionMetricsTimeToFirstActionPart,
				stopInteractionMetricsTimeToActionCompletion,
				cancelStreamingMetrics,
			} = initLatencyMetrics({ deepResearchEnabled, thinkDeeperEnabled, fastModeEnabled, agentId });

			experienceTracker.start({
				name: sendMessageExperienceNameCompiled,
				id: conversationId,
				attributes: {
					conversationId,
					interactionSource: messageSources?.interactionSource,
					featureArea: chatContextStoreActions.getIsStagingArea() ? 'aifc-staging-area' : 'unknown',
				},
			});

			const chatInitiator = getChatInitiator();

			let sendMessageContext: Awaited<ReturnType<typeof populateSendMessageContext>>;
			try {
				sendMessageContext = await populateSendMessageContext(
					chatContextStoreActions,
					isFirstPrompt,
					source,
					addCurrentPageAsContextUserPreference,
					contentContextValue,
					creationContextParams,
					productKey,
					sendAnalyticsEvent,
					contentId,
				);
			} catch (error: unknown) {
				experienceTracker.fail({
					name: sendMessageExperienceNameCompiled,
					error: error as Error,
					attributes: {
						deepResearchEnabled,
						fastModeEnabled,
						thinkDeeperEnabled,
						webSearchEnabled,
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						failureSource: 'populateSendMessageContext',
					},
				});

				// Note: We don't track error in error event as discussed here:
				// https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/341408/overview#comment-2004687398
				sendMessageLatencyMetrics.cancel();
				cancelStreamingMetrics();
				return;
			}

			if (fg('rovo_chat_analytics_attributes_contexts')) {
				const storeState = chatContextStoreActions.getAllContext();
				trackingAttributes.contextProvided = Object.entries(storeState).reduce(
					(acc, [key, value]) => {
						acc[key as keyof ChatContextStore] = Boolean(value);
						return acc;
					},
					{} as Record<keyof ChatContextStore, boolean>,
				);
				trackingAttributes.contextProvided.editorContext = Boolean(sendMessageContext.editorContext);
				trackingAttributes.contextProvided.whiteboardContext = Boolean(sendMessageContext.whiteboardContext);
				trackingAttributes.contextProvided.slidesContext = Boolean(sendMessageContext.slidesContext);
				trackingAttributes.contextProvided.databaseContext = Boolean(sendMessageContext.databaseContext);
				trackingAttributes.contextProvided.browserContext = Boolean(sendMessageContext.browserContext);
				trackingAttributes.contextProvided.dashboardContext = Boolean(sendMessageContext.dashboardContext);
				trackingAttributes.contextProvided.aiFeatureContext = Boolean(sendMessageContext.aiFeatureContext);
				trackingAttributes.contextProvided.jiraWorkflowContext = Boolean(sendMessageContext.jiraWorkflowContext);
			}

			sendAnalyticsEvent({
				eventType: 'track',
				action: 'sent',
				actionSubject: 'message',
				attributes: {
					timestamp: undefined,
					messageId: trackingAttributes.humanMessageId,
					interactionSource: trackingAttributes.interactionSource,
					followUpSource: trackingAttributes.followUpSource,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					contextProvided: trackingAttributes.contextProvided,
					initiator: chatInitiator,
					isFirstPrompt: trackingAttributes.isFirstPrompt,
					promptFeatures: trackingAttributes.promptFeatures,
					fileCount: trackingAttributes.fileCount,
					appFilters: trackingAttributes.appFilters,
					appFilterCount: trackingAttributes.appFilterCount,
					has3pApps: trackingAttributes.appFilters?.some((app) => is3pApp(app.friendlyName)),
					template: trackingAttributes.template,
					deepResearchInvoked: trackingAttributes.deepResearchInvoked,
					conversationChannelId: conversationId,
				},
			});

			const { abortController, confirmAbort, updateCurrentConversation } =
				hydrateStoreWithNewMessage(
					dispatch,
					conversationId,
					messageType,
					tmpHumanMessageId,
					tmpAssistantMessageId,
					prompt,
					files,
					agentId,
					setConversationData,
					addHumanMessage,
					addAssistantMessage,
					clearFollowUpMessages,
				);

			// Assumed the insert is successful and inserts at the last position
			const answerIndex = getMessages(conversationId).length - 1;

			// Broadcast stream status to content targets for state sharing
			const broadcastStreamLifecycle = createBroadcastStreamLifecycle({
				broadcastStreamStatus,
				chatContextStoreActions,
				contentContextValue,
				targetId: contentId,
				assistantMessageId: tmpAssistantMessageId,
			});

			// Initialize action tracking
			let streamActionInfo: StreamActionInfo = {
				isAction: false,
			};

			const minionAliasConfig = minionAlias
				? { stratus_config: { minion_alias: minionAlias } }
				: undefined;
			// Create message stream
			try {
				// Derive page context for analytics from the available context objects
				pageContext = getPageContext({
					editorContext: sendMessageContext.editorContext,
					whiteboardContext: sendMessageContext.whiteboardContext,
					databaseContext: sendMessageContext.databaseContext,
					browserUrl: sendMessageContext.browserContext?.context?.browserUrl,
				});
				trackingAttributes.pageContext = pageContext;
				trackingAttributes.invokedFrom = getInvokedFrom({
					messageActionKey: streamActionInfo?.actionKey,
				});

				// Check if the Jira refresh experiment is enabled but default is set to `true` to pass all non-Jira products
				const result = await assistanceService.sendMessageStream({
					message: prompt,
					agentId: agentId === DEFAULT_AGENT.id ? undefined : agentId,
					controller: abortController,
					conversationId,
					storeMessage: storeUserMessages,
					citationsEnabled: true,
					...(!fg('studio_fe_slos') && { minionAliasConfig }),
					fileIds: files?.map((file) => file.id),
					deepResearchEnabled,
					thinkDeeperEnabled,
					planModeEnabled: taskModeEnabled,
					alphaExtendedThinkingMode,
					fastModeEnabled,
					webSearchEnabled,
					appFilters: appFilters?.map((app) => app.source),
					browserContext: sendMessageContext.browserContext,
					editorContext: sendMessageContext.editorContext,
					whiteboardContext: sendMessageContext.whiteboardContext,
					slidesContext: sendMessageContext.slidesContext,
					databaseContext: sendMessageContext.databaseContext,
					additionalContext: sendMessageContext.additionalContext,
					aiFeatureContext: sendMessageContext.aiFeatureContext,
					jiraWorkflowContext: sendMessageContext.jiraWorkflowContext,
					dashboardContext: sendMessageContext.dashboardContext,
					dashboardEditorContext: sendMessageContext.dashboardEditorContext,
					useCurrentBrowsingContext: sendMessageContext.useCurrentBrowsingContext,
					browserContextSanitizer,
					optInToDeepResearchV2: true,
					optInThinkDeeperMessages: FeatureGates.getExperimentValue(
						'rovo_chat_thinking_deeper_messages_experiment',
						'enabled',
						false,
					),
					optInAnsNow: fg('rovo_chat_enable_ans_now'),
					optInNewToolTraces: FeatureGates.getExperimentValue(
						'rovo_chat_thinking_traces_improvements',
						'isEnabled',
						false,
						{ fireExperimentExposure: false },
					),
					optInSSEStream: true,
				});
				generator = result.generator;
				responseData = result.responseData;
			} catch (error: any) {
				if (fg('aifc_show_error_ui_for_inline_rovo')) {
					promptStreamHook?.onError?.(error, tmpAssistantMessageId);
				}
				handleSendMessageServiceCallFailure(
					error,
					deepResearchEnabled,
					thinkDeeperEnabled,
					fastModeEnabled,
					webSearchEnabled,
					streamActionInfo,
					chatContextStoreActions,
					experienceTracker,
					sendMessageExperienceNameCompiled,
					trackingAttributes,
					updateCurrentConversation,
					sendMessageLatencyMetrics,
					cancelStreamingMetrics,
					broadcastStreamLifecycle,
					confirmAbort,
					trackAIMAUEvent,
					messageSources,
					abortController,
					hasLoggedInitiatedEvent,
					tmpAssistantMessageId,
					clearMessageResearch,
					getState,
					conversationId,
					storeUserMessages,
				);
				return;
			}

			const {
				hasReceivedFinalResponse,
				hasLoggedInitiatedEvent: hasLoggedInitiatedEventForStream,
			} = await processStream({
				prompt,
				generator,
				productKey,
				answerIndex,
				tmpHumanMessageId,
				tmpAssistantMessageId,
				thinkDeeperEnabled,
				fastModeEnabled,
				deepResearchEnabled,
				webSearchEnabled,
				sendMessageExperienceNameCompiled,
				traceId: responseData?.traceId,
				updateCurrentConversation,
				trackingAttributes,
				conversationId,
				getState,
				promptStreamHook,
				openChat,
				experienceTracker,
				setMessages,
				getMessages,
				processStreamResearch,
				processMessageStreamAction,
				processMessageStreamComplete,
				processMessageStreamActionClassification,
				containerOnTempMessageIdReplaced,
				updateResearchMessageId,
				sendAnalyticsEvent,
				chatContextStoreActions,
				stopInteractionMetricsTimeToFirstActionPart,
				stopInteractionMetricsTimeToFirstAnswerPart,
				stopInteractionMetricsTimeToActionCompletion,
				broadcastStreamLifecycle,
				sendMessageLatencyMetrics,
				betweenChunksLatencyMetrics,
				cancelStreamingMetrics,
				onTempMessageIdReplacedOverride,
				streamActionInfo,
				trackAIMAUEvent,
				messageSources,
				additionalAnalyticAttributes,
				hasLoggedInitiatedEvent,
				clearMessageResearch,
				addImplicitResearchPlan,
				resetHasReceivedAnswerPart,
				viewDeadAirBroker,
				creationContextParams,
				...(fg('studio_fe_slos') && { minionAlias: assistanceService.getConfig().minionAlias }),
				...(fg('fix_minion_verification_event') && { agentId }),
				storeUserMessages,
				assistanceService,
				abortController,
				getIsChatMounted,
			});

			hasLoggedInitiatedEvent = hasLoggedInitiatedEventForStream;

			// TODO: This should be behind the !aborted logic, but due to a bug in the BE service, aborted requests
			// will client connection with the server yet still complete in the background. This results in the UI showing
			// no message updates, until reload when the actual resolved message can be sesen.
			// Once that is fixed, we can place this behind the !aborted logic.
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'received',
				actionSubject: 'agentMessage',
				attributes: {
					// messageId must remain the ID received from the server in the FINAL_RESPONSE
					// in order to map correctly to our event funnel, e.g `agentMessage received` -> `agentResponse content copied`
					messageId: trackingAttributes.messageId,
					humanMessageId: trackingAttributes.humanMessageId,
					pluginsInvoked: Array.from(trackingAttributes.pluginsInvoked),
					requestId: trackingAttributes.requestId,
					hasSources: trackingAttributes.hasSources,
					citedProductList: trackingAttributes.citedProductList,
					sourceProducts: trackingAttributes.sourceProducts,
					has3pApps: Object.keys(trackingAttributes.sourceProducts ?? {}).some(is3pApp),
					timestamp: new Date().toISOString(),
					messageActions: trackingAttributes.messageActions,
					messageDynamicUIData: trackingAttributes.messageDynamicUIData,
					singleInstrumentationID: trackingAttributes.instrumentationId,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					initiator: chatInitiator,
					deepResearchInvoked: trackingAttributes.deepResearchInvoked,
					scenarioId: trackingAttributes.scenarioId,
				},
			});

			updateCurrentConversation(clearStatus({ lastAgentMessageStatus: 'SUCCESSFUL' }));

			setMessages(conversationId, markStreamingMessagesAsComplete(getMessages(conversationId)));

			if (abortController.signal.aborted) {
				if (!hasLoggedInitiatedEvent) {
					// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
					// Delaying the initiated event firing allows us to create the pairing appropriately,
					// for any initiated events leading up to an error or dismiss state with a
					// hashed ID
					trackAIMAUEvent({
						type: 'initiated',
						subjectId: messageSources?.interactionSource,
						proactive: false,
						userGenerated: true,
						timestamp: trackingAttributes.messageSentTimeStamp,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						deepResearchEnabled: trackingAttributes.deepResearchEnabled,
						thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
						fastModeEnabled: trackingAttributes.fastModeEnabled,
						webSearchEnabled: trackingAttributes.webSearchEnabled,
						pageContext: trackingAttributes.pageContext,
					});
				}

				trackAIMAUEvent({
					type: 'dismissed',
					userGenerated: true,
					messageId: trackingAttributes.messageId,
					singleInstrumentationID: trackingAttributes.instrumentationId,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
				});

				confirmAbort();
				broadcastStreamLifecycle(StreamLifecycleState.ABORTED);

				// Call the cancel API if we have a message ID
				if (trackingAttributes.messageId) {
					assistanceService
						.cancelMessageStream({
							conversationId,
							messageId: trackingAttributes.messageId,
						})
						.catch((error: any) => {
							// Log but don't throw - the abort is already happening
							logException({
								name: 'rovoCancelMessageStream',
								exception: error,
								tags: {
									conversationId,
									messageId: trackingAttributes.messageId,
								},
							});
						});
				}

				experienceTracker.abort({
					name: sendMessageExperienceNameCompiled,
					reason: 'Aborted',
					attributes: {
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						isProgrammaticAbort: true, // always true because we're within abortController.signal.aborted (further explained in similar abort for AbortError case)
					},
				});
				experienceTracker.abort({
					name: ExperienceName.AIFC_MESSAGE,
					reason: 'stream aborted',
				});
				// Always mark incomplete researches as errored on abort — the backend can
				// autonomously trigger think-deeper even in auto mode
				markIncompleteResearchesErrored({ messageId: tmpAssistantMessageId });
				// Clear research data to remove lingering think deeper state
				clearMessageResearch({ messageId: tmpAssistantMessageId });
				return;
			}

			// Only fire if the request wasn't aborted
			else {
				if (!hasLoggedInitiatedEvent) {
					// See: https://hello.atlassian.net/wiki/spaces/~321863207/pages/4188127855/Analytics+2024-11-16
					// Delaying the initiated event firing allows us to create the pairing appropriately,
					// for any initiated events leading up to an error or dismiss state with a
					// hashed ID
					trackAIMAUEvent({
						type: 'initiated',
						subjectId: messageSources?.interactionSource,
						proactive: false,
						userGenerated: true,
						timestamp: trackingAttributes.messageSentTimeStamp,
						singleInstrumentationID: trackingAttributes.instrumentationId,
						deepResearchEnabled: trackingAttributes.deepResearchEnabled,
						thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
						fastModeEnabled: trackingAttributes.fastModeEnabled,
						webSearchEnabled: trackingAttributes.webSearchEnabled,
						pageContext: trackingAttributes.pageContext,
					});
				}

				//If we're done processing the stream and no answer part has been processed, then it means that the BE is hanging. Throw an error
				if (!hasReceivedFinalResponse) {
					const agentDetails = await assistanceService.getAgentDetails(agentId);
					if (agentDetails.deactivated) {
						// If agent is deactivated, throw correct error
						const agentError = new NoFinalResponseError(
							'The selected agent may no longer be available.',
							{
								traceId: responseData?.traceId,
							},
						);

						experienceTracker.fail({
							name: sendMessageExperienceNameCompiled,
							error: agentError,
							attributes: {
								deepResearchEnabled,
								thinkDeeperEnabled,
								fastModeEnabled,
								webSearchEnabled,
								llmDebugLogsEnabled: storeUserMessages,
								isStreamAction: streamActionInfo.isAction,
								actionKey: streamActionInfo.actionKey,
								featureArea: chatContextStoreActions.getIsStagingArea()
									? 'aifc-staging-area'
									: 'unknown',
							},
						});

						trackAIMAUEvent({
							type: 'error',
							userGenerated: true,
							messageId: trackingAttributes.requestId,
							timestamp: new Date().toISOString(),
							singleInstrumentationID: trackingAttributes.instrumentationId,
							deepResearchEnabled: trackingAttributes.deepResearchEnabled,
							thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
							fastModeEnabled: trackingAttributes.fastModeEnabled,
							webSearchEnabled: trackingAttributes.webSearchEnabled,
							attributes: {
								...(creationContextParams?.dynamicUiType && {
									dynamicUiType: creationContextParams.dynamicUiType,
								}),
								...(creationContextParams?.dynamicUiSubtype && {
									dynamicUiSubtype: creationContextParams.dynamicUiSubtype,
								}),
								...(trackingAttributes?.invokedFrom && {
									invokedFrom: trackingAttributes.invokedFrom,
								}),
								...(streamActionInfo?.actionKey && {
									actionKey: streamActionInfo.actionKey,
								}),
							},
						});

						updateCurrentConversation({
							status: CHAT_STORE_STATUS.deactivatedAgentError,
							traces: [],
						});

						sendMessageLatencyMetrics.cancel();
						cancelStreamingMetrics();
						broadcastStreamLifecycle(StreamLifecycleState.ERRORED);
						return;
					} else {
						// Agent is active, BE hanging
						const timeoutError = new NoFinalResponseError(
							'The response took too long and timed out. Try refreshing the page.',
							{
								traceId: responseData?.traceId,
							},
						);

						experienceTracker.fail({
							name: sendMessageExperienceNameCompiled,
							error: timeoutError,
							attributes: {
								deepResearchEnabled,
								thinkDeeperEnabled,
								fastModeEnabled,
								webSearchEnabled,
								llmDebugLogsEnabled: storeUserMessages,
								isStreamAction: streamActionInfo.isAction,
								actionKey: streamActionInfo.actionKey,
								featureArea: chatContextStoreActions.getIsStagingArea()
									? 'aifc-staging-area'
									: 'unknown',
							},
						});

						trackAIMAUEvent({
							type: 'error',
							userGenerated: true,
							messageId: trackingAttributes.requestId,
							timestamp: new Date().toISOString(),
							singleInstrumentationID: trackingAttributes.instrumentationId,
							deepResearchEnabled: trackingAttributes.deepResearchEnabled,
							thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
							fastModeEnabled: trackingAttributes.fastModeEnabled,
							webSearchEnabled: trackingAttributes.webSearchEnabled,
							attributes: {
								...(creationContextParams?.dynamicUiType && {
									dynamicUiType: creationContextParams.dynamicUiType,
								}),
								...(creationContextParams?.dynamicUiSubtype && {
									dynamicUiSubtype: creationContextParams.dynamicUiSubtype,
								}),
								...(trackingAttributes?.invokedFrom && {
									invokedFrom: trackingAttributes.invokedFrom,
								}),
								...(streamActionInfo?.actionKey && {
									actionKey: streamActionInfo.actionKey,
								}),
							},
						});

						sendMessageLatencyMetrics.cancel();
						cancelStreamingMetrics();
						broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

						throw timeoutError;
					}
				}

				/**
				 * IMPORTANT: CRITICAL ANALYTICS EVENT
				 *
				 * This event is essential for business metrics and monitoring.
				 *
				 * DO NOT MODIFY the structure, content, or emission logic of this event without:
				 *   - Careful review of potential impacts on data quality, privacy, and business reporting.
				 *   - Explicit approval from both analytics and security stakeholders.
				 *
				 * Reference: Incident HOT-120175 (UGC page title and URLs leak)
				 * Event Registry: https://data-portal.internal.atlassian.com/analytics/registry/68936?tabIndex=Details
				 */
				trackAIMAUEvent({
					type: 'viewed',
					subjectId: messageSources?.followUpSource,
					messageId: trackingAttributes.messageId,
					source: messageSources?.interactionSource,
					messageActions: trackingAttributes.messageActions,
					messageDynamicUIData: trackingAttributes.messageDynamicUIData,
					invokedFrom: trackingAttributes.invokedFrom,
					proactive: false,
					userGenerated: true,
					singleInstrumentationID: trackingAttributes.instrumentationId,
					timestamp: new Date().toISOString(),
					citedProductList: trackingAttributes.citedProductList,
					sourceProducts: trackingAttributes.sourceProducts,
					pluginsInvoked: Array.from(trackingAttributes.pluginsInvoked),
					skillsInvoked: trackingAttributes.skillsInvoked,
					executedStratusSkill: trackingAttributes.executedStratusSkill,
					responseSources: trackingAttributes.sources,
					usesJsm: trackingAttributes.usesJsm,
					deepResearchEnabled: trackingAttributes.deepResearchEnabled,
					thinkDeeperEnabled: trackingAttributes.thinkDeeperEnabled,
					fastModeEnabled: trackingAttributes.fastModeEnabled,
					webSearchEnabled: trackingAttributes.webSearchEnabled,
					conversationChannelId: conversationId,
					scenarioId: trackingAttributes.scenarioId,
					pageContext: trackingAttributes.pageContext,
					attributes: {
						...(creationContextParams?.dynamicUiType && {
							dynamicUiType: creationContextParams.dynamicUiType,
						}),
						...(creationContextParams?.dynamicUiSubtype && {
							dynamicUiSubtype: creationContextParams.dynamicUiSubtype,
						}),
						...(additionalAnalyticAttributes && additionalAnalyticAttributes),
					},
				});

				// Experience is aborted in `abortRequests`
				experienceTracker.succeed({
					name: sendMessageExperienceNameCompiled,
				});

				// Broadcast that the stream has completed successfully
				broadcastStreamLifecycle(StreamLifecycleState.COMPLETED);

				if (fg('rovo_chat_chat_history_refresh')) {
					//If user is viewing a different conversation or not viewing ConversationAssitant, optimistically set conversation to unread
					if (getState().currentConversationId !== conversationId) {
						dispatch(
							setConversationData({
								conversationId,
								data: { readStatus: false },
							}),
						);
					}
				}
			}

			sendMessageLatencyMetrics.stop({
				customData: messageSources?.interactionSource
					? { interactionSource: messageSources.interactionSource }
					: undefined,
			});
		};

		const conversationId = conversationIdParam ?? getState().currentConversationId;

		try {
			await sendMessageFn({ conversationId });
		} catch (error: any) {
			// Legacy reconnection path: handle StreamDisconnectedError thrown by processStream
			// when rovo_chat_reconnectable_stream is OFF.
			if (error instanceof StreamDisconnectedError) {
				const reconnectionOnComplete = (result: ReconnectCompletionResult) => {
					if (result.success) {
						const meta = result.streamMeta;
						trackAIMAUEvent({
							type: 'viewed',
							subjectId: messageSources?.followUpSource,
							messageId: error.agentMessageId,
							source: messageSources?.interactionSource,
							messageActions: meta?.messageActions,
							proactive: false,
							userGenerated: true,
							singleInstrumentationID: error.agentMessageId,
							timestamp: new Date().toISOString(),
							deepResearchEnabled,
							thinkDeeperEnabled,
							fastModeEnabled,
							webSearchEnabled,
							conversationChannelId: error.conversationId,
							citedProductList: meta?.citedProductList ?? {},
							sourceProducts: meta?.sourceProducts ?? {},
							pluginsInvoked: meta?.pluginsInvoked,
							skillsInvoked: meta?.skillsInvoked,
							responseSources: meta?.responseSources,
							usesJsm: meta?.usesJsm,
							scenarioId: meta?.scenarioId,
							pageContext,
						});
						experienceTracker.succeed({
							name: sendMessageExperienceNameCompiled,
						});
					} else {
						sendAnalyticsEvent({
							eventType: 'track',
							action: 'failed',
							actionSubject: 'rovoChatStream',
							attributes: {
								isDeepResearchEnabled: deepResearchEnabled,
								isThinkDeeperEnabled: thinkDeeperEnabled,
								isWebSearchEnabled: webSearchEnabled,
								isAIFCStreaminAction: false,
								isReconnectable: true,
								conversationId,
								instrumentationId: error.agentMessageId,
								requestId: error.agentMessageId,
								interactionSource: messageSources?.interactionSource,
								contentContext: chatContextStoreActions.getIsStagingArea()
									? 'staging-area'
									: 'global',
								userAgent: navigator?.userAgent,
								error: error.message,
							},
						});
						trackAIMAUEvent({
							type: 'error',
							userGenerated: true,
							messageId: error.agentMessageId,
							singleInstrumentationID: error.agentMessageId,
							timestamp: new Date().toISOString(),
							deepResearchEnabled,
							thinkDeeperEnabled,
							fastModeEnabled,
							webSearchEnabled,
						});
						experienceTracker.fail({
							name: sendMessageExperienceNameCompiled,
							error: result.error ?? new Error('Reconnection failed'),
							attributes: {
								deepResearchEnabled,
								thinkDeeperEnabled,
								fastModeEnabled,
								webSearchEnabled,
								llmDebugLogsEnabled: storeUserMessages,
								featureArea: chatContextStoreActions.getIsStagingArea()
									? 'aifc-staging-area'
									: 'unknown',
							},
						});
					}
				};

				await dispatch(
					reconnectToInProgressStream({
						conversationId: error.conversationId,
						messageId: error.agentMessageId,
						answerIndex: error.answerIndex,
						isDeepResearch: deepResearchEnabled,
						lastEventId: error.lastEventId,
						abortCurrentRequest: true,
						onComplete: reconnectionOnComplete,
					}),
				);

				return;
			}

			if (isChannelNotFoundError(error)) {
				const createdChannel = await assistanceService.createConversationChannel();

				setState({
					conversations: {
						...getState().conversations,
						[createdChannel.id]: {
							id: createdChannel.id,
							name: createdChannel.name,
							timeCreated: createdChannel.time_created,
							status: CHAT_STORE_STATUS.idle,
							traces: [],
							messages: [],
							...(createdChannel.agent && fg('rovo_chat_chat_history_refresh')
								? { agent: createdChannel.agent }
								: {}),
						},
					},
				});

				setMessages(createdChannel.id, []);

				dispatch(
					setSelectedConversation({
						conversationId: createdChannel.id,
					}),
				);

				await sendMessageFn({ conversationId: createdChannel.id });
			} else {
				throw error;
			}
		}
	};

type InternalStreamStats = {
	totalChunks: number;
	traceChunks: number;
	answerChunks: number;
	actionChunks: number;
	researchChunks: number;
	otherChunks: number;
	firstChunkAt?: number;
	lastChunkAt?: number;
	sumInterChunkMs: number;
	pluginsObserved: Set<string>;
};

const getStreamStats = () => {
	const stats: InternalStreamStats = {
		totalChunks: 0,
		traceChunks: 0,
		answerChunks: 0,
		actionChunks: 0,
		researchChunks: 0,
		otherChunks: 0,
		firstChunkAt: undefined,
		lastChunkAt: undefined,
		sumInterChunkMs: 0,
		pluginsObserved: new Set<string>(),
	};

	const update = (streamMessage: StreamMessage) => {
		const now = performance.now();
		stats.totalChunks += 1;
		if (stats.firstChunkAt === undefined) {
			stats.firstChunkAt = now;
		}
		if (stats.lastChunkAt !== undefined) {
			stats.sumInterChunkMs += now - stats.lastChunkAt;
		}
		stats.lastChunkAt = now;

		if (isStreamTrace(streamMessage)) {
			stats.traceChunks += 1;
		} else if (isStreamAnswerPart(streamMessage)) {
			stats.answerChunks += 1;
		} else if (isStreamResearchPayload(streamMessage)) {
			stats.researchChunks += 1;
		} else if (isStreamActionPart(streamMessage) || isStreamActionPrepare(streamMessage)) {
			// Count action-related chunks (prepare/part)
			stats.actionChunks += 1;
		} else {
			// Count unknown chunks
			stats.otherChunks += 1;
		}

		// Observe plugin names if present early (often only present on final response)
		const invocations = isStreamResponse(streamMessage)
			? streamMessage.message?.message?.plugin_invocations
			: [];
		if (Array.isArray(invocations)) {
			invocations.forEach((p: any) => p?.plugin_name && stats.pluginsObserved.add(p.plugin_name));
		}
	};

	const getStats = () => {
		const avgGapMs =
			stats.totalChunks > 1 ? Math.round(stats.sumInterChunkMs / (stats.totalChunks - 1)) : 0;
		return {
			...stats,
			pluginsObserved: Array.from(stats.pluginsObserved),
			averageGapMs: avgGapMs,
		};
	};

	return { update, getStats };
};

export type InactivityWatchdogConfig = {
	baseMs: number;
	sendAnalyticsEvent: (payload: AnalyticsEventPayload) => void;
};

export const createBetweenChunksInactivityLogger = (config: InactivityWatchdogConfig) => {
	let armed = false;
	let firstChunkArrived = false;
	let timer: number | undefined;
	let stallCount = 0;

	const clearTimer = () => {
		if (timer !== undefined) {
			clearTimeout(timer);
			timer = undefined;
		}
	};

	const schedule = (stage: 'newResponse' | 'firstGap' | 'betweenChunks') => {
		clearTimer();
		const timeoutMs = config.baseMs;

		timer = setTimeout(() => {
			stallCount += 1;

			config.sendAnalyticsEvent({
				eventType: 'track',
				action: 'timeout',
				actionSubject: 'rovoChatStream',
				attributes: {
					stallCount,
					stage,
					timeoutMs,
				},
			});
			// Keep armed so we continue to detect subsequent stalls until chunks resume
			schedule('betweenChunks');
		}, timeoutMs) as unknown as number;
	};

	return {
		arm: (): void => {
			if (armed) {
				return;
			}
			armed = true;
			// Start pre-first-chunk watchdog waiting for the first stream event
			schedule('newResponse');
		},
		onChunk: (): void => {
			if (!armed) {
				return;
			}
			if (firstChunkArrived === false) {
				firstChunkArrived = true;
				// After the first chunk, schedule timeout waiting for the next event
				schedule('firstGap');
				return;
			}
			// For subsequent chunks, re-arm the between-chunks watchdog
			schedule('betweenChunks');
		},
		stop: (): void => {
			clearTimer();
			armed = false;
		},
	};
};
